<template>
  <div class="pension-calc">

    <!-- ═══════════════════════════════════════════════════════════
         HEADER + DISCLAIMER
    ═══════════════════════════════════════════════════════════ -->
    <header class="pc-header">
      <h1>Pension Value Estimator</h1>
      <p class="pc-intro">
        Estimate the value of a defined-benefit pension earned during marriage or cohabitation,
        so you can see whether it's significant enough to be worth offsetting against other family
        assets — for example, so one spouse can keep the home. Enter the information below to generate
        estimates across several retirement scenarios; if the value looks material, obtain a formal
        actuarial valuation before settling.
      </p>
      <div class="pc-disclaimer">
        <strong>Important disclaimer:</strong>
        These values are estimates only and should not be relied upon for legal,
        financial, or settlement decisions. This tool is not a substitute for a professional
        actuarial valuation and its results are not legally recognized or admissible in court.
        For a professional valuation, consult a qualified actuary.
        <br /><br />
        <strong>Federal plans:</strong> for federal pensions (PSSA, Canadian Forces, RCMP) divided
        under the <em>Pension Benefits Division Act</em>, the plan administrator provides the official
        division value at no cost.
      </div>
      <div class="pc-onbanner" v-if="isOntario">
        <strong>Ontario (prescribed Family Law Value).</strong>
        This plan is Ontario-registered, so it is valued as the prescribed <strong>Family Law
        Value</strong> (FSRA Form FL-4B / FL-4E) as at the <strong>separation date</strong>, on a
        termination basis. <strong>Preliminary estimate only</strong> — the official FLV is issued
        by the plan administrator.
      </div>
      <div class="pc-warnbanner" v-if="isManitoba">
        <strong>Manitoba:</strong> Manitoba pensions are generally <strong>divided at source</strong> —
        the plan splits the pension earned during the relationship rather than using a present-value
        setoff. This estimate uses the present-value approach and <strong>may not reflect</strong> how
        a Manitoba plan is actually divided. Confirm the division with the plan administrator.
      </div>
    </header>

    <!-- Report toolbar — full-width bar above the report. -->
    <div v-if="view === 'report'" class="report-toolbar">
      <button class="btn-ghost-sm" type="button" @click="view = 'entry'">↤ Back to form to edit</button>
      <span class="report-toolbar-hint">All inputs are summarised below — return to the form to make changes.</span>
    </div>

    <div class="pc-shell" :class="view === 'report' ? 'pc-shell--report' : 'pc-shell--entry'">
      <!-- ═════════════════════════════════════════════════════════
           INPUT FORM — full-width 2-col grid (entry page only). In the report
           view the inputs are summarised in the Summary of Data card instead.
      ═════════════════════════════════════════════════════════ -->
      <!-- DIVII PATCH: the matter's pension is chosen here; the host keeps one
           saved valuation per pension and pre-selects the pension's owner as
           the client. Outside the fieldset so view-only roles can still switch. -->
      <section class="step" v-if="view === 'entry' && pensionOptionsProp !== null">
        <h2>Pension Being Valued</h2>
        <div class="field client-field" v-if="pensionOptionsProp.length">
          <label>Pension being valued <span class="req">*</span></label>
          <select :value="selectedPensionIndexProp" @change="onSelectPension">
            <option :value="null" disabled>Select a pension…</option>
            <option v-for="opt in pensionOptionsProp" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
          </select>
          <span class="hint" v-if="selectedPensionIndexProp === null">Inputs and results are saved with the pension you choose, and can be recorded in the agreement.</span>
          <span class="hint" v-else>Inputs and results are saved with this pension. Pick another pension to value it separately.</span>
        </div>
        <div class="field client-field" v-else>
          <label>Pension being valued</label>
          <span class="hint">No pensions have been added yet. Add the pension in the Pensions section of the builder, then return here to value it and record the result.</span>
        </div>
      </section>
      <!-- DIVII FIX — fieldset so Divii's view-only role disables every input. -->
      <fieldset v-if="view === 'entry'" class="pc-inputs pc-inputs--entry" :disabled="readOnly">

        <!-- Personal information — sourced from Divii via the client dropdown -->
        <section class="step">
          <h2>Personal Pension Information</h2>
          <p class="sub">Select the pension being valued and the plan member; their details are loaded from Divii.</p>
          <div class="field client-field">
            <label>Client <span class="req">*</span></label>
            <select v-model.number="selectedClientIdx">
              <option :value="null" disabled>Select a client…</option>
              <option v-for="(c, i) in clients" :key="i" :value="i">{{ c.name }}</option>
            </select>
          </div>
          <div class="readout-grid readout-grid--3">
            <div class="readout">
              <span class="readout-label">Date of Birth</span>
              <span class="readout-value readout-value--sm">{{ birthdate ? fmtDate(birthdate) : '—' }}</span>
            </div>
            <div class="readout">
              <span class="readout-label">Gender</span>
              <span class="readout-value readout-value--sm">{{ gender || '—' }}</span>
            </div>
            <div class="readout">
              <span class="readout-label">Current Age</span>
              <span class="readout-value">{{ currentAge ? fmt2(currentAge) : '—' }}<small v-if="currentAge"> yrs</small></span>
            </div>
          </div>
        </section>

        <!-- Relationship details — also from the Divii matter (relationship dates) -->
        <section class="step">
          <h2>Relationship Details</h2>
          <div class="rel-layout rel-layout--stacked">
            <div class="rel-inputs">
              <!-- Read-only — sourced from the Divii matter (the builder), shown
                   like the Personal Information readouts. -->
              <div class="readout-grid">
                <div class="readout">
                  <span class="readout-label">Marriage / Cohabitation Date</span>
                  <span class="readout-value readout-value--sm">{{ cohabitationDate ? fmtDate(cohabitationDate) : '—' }}</span>
                </div>
                <div class="readout">
                  <span class="readout-label">Separation Date</span>
                  <span class="readout-value readout-value--sm">{{ separationDate ? fmtDate(separationDate) : '—' }}</span>
                </div>
              </div>
              <p class="hint" v-if="cohabBeforeHire">
                ⚠️ The cohabitation date is before the hire date. Only the period after the hire
                date is counted toward the pension valuation.
              </p>
              <p class="warn-box" v-if="relationshipUnderTwoYears">
                ⚠️ Pension value may not be applicable due to the duration of the relationship.
              </p>
            </div>
            <div class="rel-outputs">
              <div class="readout-grid" v-if="yearsMarried > 0">
                <div class="readout readout--wide">
                  <span class="readout-label">Relationship period relevant to pension</span>
                  <span class="readout-value readout-value--sm">
                    {{ fmtDate(effectiveCohabitationDate) }} → {{ fmtDate(separationDate) }}
                  </span>
                </div>
                <div class="readout readout--wide">
                  <span class="readout-label">Years in relationship during pension</span>
                  <span class="readout-value">{{ fmt2(yearsMarried) }}<small> yrs</small></span>
                </div>
              </div>
              <p class="warn-box" v-else-if="cohabitationDate && separationDate">
                The relationship period doesn't overlap the pension membership, or the dates
                entered result in 0 years.
              </p>
            </div>
          </div>
        </section>

        <!-- Employment information -->
        <section class="step">
          <h2>Employment Information</h2>

          <!-- Pension plan jurisdiction — drives the valuation regime. Seeded
               from the matter's province of residence but separately editable. -->
          <div class="field">
            <label>Pension Plan Jurisdiction</label>
            <select v-model="planJurisdiction" class="sector-select">
              <option value="">— Select —</option>
              <option value="BC">British Columbia</option>
              <option value="AB">Alberta</option>
              <option value="SK">Saskatchewan</option>
              <option value="MB">Manitoba</option>
              <option value="ON">Ontario</option>
              <option value="NB">New Brunswick</option>
              <option value="NS">Nova Scotia</option>
              <option value="PE">Prince Edward Island</option>
              <option value="NL">Newfoundland &amp; Labrador</option>
              <option value="FED">Federal / Territories</option>
            </select>
            <span class="hint">
              Where the <strong>plan</strong> is registered — may differ from the member's residence.
              Drives the valuation regime (Ontario uses a prescribed Family Law Value).
            </span>
            <span class="hint" v-if="isFederal">
              Federal pensions are <strong>fully CPI-indexed</strong> and government-sector, so the
              defaults are set to full indexation and public-sector mortality (don't apply the CPI−0.5%
              haircut used for some B.C. plans). Screening estimate only — the official division
              value is free from the plan administrator.
            </span>
          </div>

          <!-- Pension status — drives the whole valuation method -->
          <div class="field">
            <label>Pension Status</label>
            <div class="radio-row">
              <label><input type="radio" value="working" v-model="pensionStatus" /> Still working / not yet retired</label>
              <!-- DIVII PATCH: former member with a deferred (vested) pension: left the
                   employer, no further accrual, pension not yet in pay. -->
              <label><input type="radio" value="deferred" v-model="pensionStatus" /> Stopped working, not yet retired</label>
              <label><input type="radio" value="inpay" v-model="pensionStatus" /> Already retired — pension in pay</label>
            </div>
          </div>
          <div class="field" v-if="!isInPay && !isOntario">
            <label>Pension Amount Source</label>
            <div class="radio-row">
              <label><input type="radio" value="statement" v-model="pensionSource" /> From pension statement (recommended)</label>
              <label><input type="radio" value="formula" v-model="pensionSource" /> Estimate from salary &amp; service</label>
            </div>
          </div>

          <!-- Date of Hire (entered here — not held in Divii), retirement date
               (in-pay only), and Years of Service -->
          <div class="rel-layout">
            <div class="rel-inputs">
              <div class="field">
                <label>Date of Hire <span class="req">*</span></label>
                <date-field v-model="hireDate" :disabled="readOnly" />
              </div>
              <div class="field" v-if="isInPay">
                <label>Retirement / Pension Start Date <span class="req">*</span></label>
                <date-field v-model="retirementDate" :disabled="readOnly" />
              </div>
              <!-- DIVII PATCH: former member: service froze on the day they left. -->
              <div class="field" v-if="isDeferred">
                <label>Employment End Date <span class="req">*</span></label>
                <date-field v-model="terminationDate" :disabled="readOnly" />
                <span class="hint">The last day of plan membership. Service and the relationship overlap are counted to this date.</span>
              </div>
            </div>
            <div class="rel-outputs">
              <div class="readout-grid">
                <div class="readout readout--wide">
                  <span class="readout-label">Years of Service</span>
                  <span class="readout-value">{{ totalServiceYears ? fmt2(totalServiceYears) : '—' }}<small v-if="totalServiceYears"> yrs</small></span>
                </div>
              </div>
            </div>
          </div>

          <!-- Working + salary formula: salary + expected growth -->
          <div v-if="!isInPay && !isOntario && pensionSource === 'formula'" class="fields fields--align-input emp-salary-row">
            <div class="field">
              <!-- DIVII PATCH: former member: the pension is built on earnings at the leaving date. -->
              <label>{{ isDeferred ? 'Annual Salary at Employment End' : 'Current Annual Salary' }} <span class="req">*</span></label>
              <div class="input-prefix">
                <span>$</span>
                <input type="number" min="0" step="1000" v-model.number="currentSalary" placeholder="0.00" />
              </div>
              <span class="hint" v-if="currentSalary > 0">Current value: <strong>{{ fmtMoney(currentSalary) }}</strong></span>
              <span class="hint" v-else-if="isDeferred">Pensionable earnings when the member left (the plan's highest-average figure if known).</span>
            </div>
            <div class="field" v-if="!isDeferred">
              <label>Expected Annual Salary Growth — {{ expectedSalaryGrowth.toFixed(1) }}%</label>
              <input type="range" min="0" max="10" step="0.1" v-model.number="expectedSalaryGrowth" class="slider" />
            </div>
          </div>

          <!-- In pay: the known annual pension + optional bridge to 65 -->
          <div v-if="isInPay" class="fields fields--align-input emp-salary-row">
            <div class="field">
              <label>{{ isOntario ? 'Annual Lifetime Pension at Separation Date' : 'Annual Lifetime Pension in Pay' }} <span class="req">*</span></label>
              <div class="input-prefix">
                <span>$</span>
                <input type="number" min="0" step="1000" v-model.number="annualPensionInPay" placeholder="0.00" />
              </div>
              <span class="hint" v-if="annualPensionInPay > 0">Current value: <strong>{{ fmtMoney(annualPensionInPay) }}</strong></span>
              <span class="hint" v-else-if="isOntario">The pension being received as at the separation date (FL-4E).</span>
            </div>
            <div class="field">
              <label>Monthly Bridge to Age 65 (if any)</label>
              <div class="input-prefix">
                <span>$</span>
                <input type="number" min="0" step="10" v-model.number="bridgeInPayMonthly" placeholder="0" />
              </div>
            </div>
          </div>
        </section>

        <!-- Pension estimates from the statement (active member, recommended) -->
        <section class="step" v-if="!isInPay && !isOntario && pensionSource === 'statement'">
          <h2>Pension Estimates (from statement)</h2>

          <!-- DIVII PATCH: a former member has already terminated, so the basis is fixed. -->
          <div class="field" v-if="!isDeferred">
            <label>Which is most likely for this member?</label>
            <div class="radio-row radio-row--stack">
              <label><input type="radio" value="terminate" v-model="valuationBasis" /> Most likely to <strong>terminate</strong> employment (value service to date — FLA default)</label>
              <label><input type="radio" value="continue" v-model="valuationBasis" /> Most likely to <strong>continue to retirement</strong> (project service &amp; salary)</label>
            </div>
          </div>

          <p class="sub" v-if="isDeferred">
            Enter the <strong>deferred pension</strong> monthly figures from the former member's statement for
            each retirement age (Single Life Guaranteed 10 Years). Service stopped on the employment end date, so
            the marital share is based on service to that date, and the deferred pension is indexed at CPI to commencement.
          </p>
          <p class="sub" v-else-if="valuationBasis === 'terminate'">
            Enter the statement's <strong>"if you stopped working" (deferred)</strong> monthly figures for
            each retirement age (Single Life Guaranteed 10 Years). The marital share is based on service
            earned to today, and the deferred pension is indexed at CPI to commencement.
          </p>
          <p class="sub" v-else>
            Enter the statement's <strong>"if you continue to work"</strong> monthly figures for each
            retirement age (Single Life Guaranteed 10 Years). Service and salary are projected to retirement
            and the marital share is over projected service. Lifetime is the ongoing pension; bridge is the
            temporary top-up paid only until age 65.
          </p>
          <div class="field wage-growth-field" v-if="!isDeferred && valuationBasis === 'continue'">
            <label>Wage Growth to Retirement — {{ expectedSalaryGrowth.toFixed(1) }}%</label>
            <input type="range" min="0" max="10" step="0.1" v-model.number="expectedSalaryGrowth" class="slider" />
            <span class="hint">Escalates the statement's (current-dollar) estimates to the retirement date.</span>
          </div>
          <div class="stmt-head">
            <span>Retire at age</span><span>Lifetime $/mo <span class="req">*</span></span><span>Bridge $/mo (to 65)</span><span>Tax %</span><span></span>
          </div>
          <div v-for="(sc, i) in statementScenarios" :key="'st' + i" class="stmt-row">
            <input type="number" min="50" max="71" step="1" v-model.number="sc.age" />
            <div class="input-prefix"><span>$</span><input type="number" min="0" step="10" v-model.number="sc.lifetimeMonthly" /></div>
            <div class="input-prefix"><span>$</span><input type="number" min="0" step="10" v-model.number="sc.bridgeMonthly" /></div>
            <input type="number" min="0" max="50" step="1" v-model.number="sc.taxRate" />
            <button class="btn-x" type="button" @click="removeStatement(i)" title="Remove" :disabled="statementScenarios.length <= 1">×</button>
          </div>
          <button class="btn-ghost btn-block" type="button" @click="addStatement">+ Add Another Retirement Age</button>
        </section>

        <!-- Ontario FLV (active member, FL-4B) — deferred figures at the FLVD -->
        <section class="step" v-if="!isInPay && isOntario">
          <h2>Accrued Pension at Separation (Ontario {{ ontarioFormCode }})</h2>
          <!-- DIVII PATCH: former member (FL-4D): the deferred pension is already fixed. -->
          <p class="sub" v-if="isDeferred">
            Enter the <strong>deferred pension the former member is entitled to</strong>, by commencement
            age, straight from the plan's statement or the FL-1 figures. Service and salary froze on the
            employment end date. The value is computed as at the separation date (the Family Law
            Valuation Date) on the CIA commuted-value basis (§3500).
          </p>
          <p class="sub" v-else>
            Enter the <strong>deferred pension you would receive if you terminated on the separation
            date</strong> (the Family Law Valuation Date), by commencement age — straight from the
            member's statement or the FL-1 figures. Service and salary are frozen at the FLVD. The
            value is computed on the CIA commuted-value basis (§3500).
          </p>
          <!-- DIVII PATCH: former member who left AFTER the separation date. The
               FL-4 value is on a termination-at-FLVD basis, so an entitlement dated
               at the (later) leaving date is prorated back unless the lawyer has
               the plan's FLVD-dated figures. -->
          <div class="field" v-if="isDeferred && ontarioLeftAfterSeparation">
            <p class="sub">
              Employment ended <strong>after</strong> the separation date, so an entitlement dated at the
              leaving date includes service earned after the Family Law Valuation Date.
              <template v-if="!ontarioFiguresAtFLVD">
                The figures above are prorated by service to the FLVD ({{ fmt2(serviceToFLVD) }} of
                {{ fmt2(ontarioTotalService) }} years, {{ fmtPct(ontarioDeferredProration) }}) to approximate
                the termination-at-FLVD basis the FL-4 uses.
              </template>
              <template v-else>They are used as entered.</template>
            </p>
            <label class="checkbox-line">
              <input type="checkbox" v-model="ontarioFiguresAtFLVD" />
              These figures are already as at the separation date (from the plan's FL-4 statement), do not prorate
            </label>
          </div>
          <div class="field">
            <label class="checkbox-line">
              <input type="checkbox" v-model="ontarioSubsidized" />
              Eligible for a <strong>subsidized unreduced early</strong> pension (applies the CIA 50/50 rule)
            </label>
            <span class="hint">
              If checked, the FLV is the average of the value at the highest-value age and at the
              earliest-unreduced age. If unchecked, the highest-value age is used.
            </span>
          </div>
          <div class="field" v-if="ontarioSubsidized">
            <label>Earliest Unreduced Age</label>
            <input type="number" min="50" max="71" step="1" v-model.number="ontarioEarliestUnreducedAge" class="ont-eu-input" />
            <span class="hint">Must match one of the commencement ages below.</span>
          </div>
          <div class="stmt-head stmt-head--ont">
            <span>Commencement age</span><span>Deferred lifetime $/mo <span class="req">*</span></span><span>Bridge $/mo (to 65)</span><span></span>
          </div>
          <div v-for="(sc, i) in ontarioScenarios" :key="'ont' + i" class="stmt-row stmt-row--ont">
            <input type="number" min="50" max="71" step="1" v-model.number="sc.age" />
            <div class="input-prefix"><span>$</span><input type="number" min="0" step="10" v-model.number="sc.lifetimeMonthly" /></div>
            <div class="input-prefix"><span>$</span><input type="number" min="0" step="10" v-model.number="sc.bridgeMonthly" /></div>
            <button class="btn-x" type="button" @click="removeOntario(i)" title="Remove" :disabled="ontarioScenarios.length <= 1">×</button>
          </div>
          <button class="btn-ghost btn-block" type="button" @click="addOntario">+ Add Another Commencement Age</button>
        </section>

        <!-- Retirement scenarios (active member, salary-formula path) -->
        <section class="step" v-if="!isInPay && !isOntario && pensionSource === 'formula'">
          <h2>{{ isDeferred ? 'Pension Start Scenarios' : 'Retirement Scenarios' }}</h2>
          <!-- DIVII PATCH: former member: the fixed scenarios collapse to one per start age. -->
          <p class="sub" v-if="isDeferred">The deferred pension is fixed; each scenario starts it at a different age. Add custom start ages below.</p>
          <p class="sub" v-else>Scenarios 1–3 are fixed. Add custom retirement ages below.</p>

          <div class="scenario-fixed">
            <div v-for="(s, i) in visibleFixedScenarios" :key="'fx' + i" class="scenario-fixed-row">
              <span class="scenario-fixed-name">{{ i + 1 }}. {{ s.label }}</span>
            </div>
          </div>

          <h3>{{ isDeferred ? 'Custom Start Ages' : 'Custom Retirement Ages' }}</h3>
          <div class="custom-age-list">
            <div v-for="(c, i) in customScenarios" :key="'c' + i" class="custom-age-row">
              <div class="field custom-age-field">
                <label>Custom Age {{ i + 1 }}</label>
                <input type="number" min="55" max="100" step="1" v-model.number="c.age" />
              </div>
              <button class="btn-x" type="button" @click="removeCustom(i)" title="Remove">×</button>
            </div>
          </div>
          <button class="btn-ghost btn-block" type="button" @click="addCustom">+ Add Another Retirement Age</button>
        </section>

        <!-- Calculation assumptions (CIA-style actuarial basis) — collapsible -->
        <section class="step">
          <h2 class="step-toggle" @click="assumptionsOpen = !assumptionsOpen"
              role="button" tabindex="0" @keydown.enter="assumptionsOpen = !assumptionsOpen"
              :aria-expanded="assumptionsOpen ? 'true' : 'false'">
            <span>{{ isOntario ? 'Commuted Value Basis (CIA §3500)' : 'Calculation Assumptions' }}</span>
            <span class="step-chevron" :class="{ open: assumptionsOpen }">▸</span>
          </h2>
          <p class="assump-summary" v-if="!assumptionsOpen"
             @click="assumptionsOpen = true" role="button" tabindex="0"
             @keydown.enter="assumptionsOpen = true">
            {{ assumptionsSummary }} — <span class="assump-adjust">click to adjust</span>
          </p>
          <div v-show="assumptionsOpen">
          <p class="sub" v-if="isOntario">
            Ontario FLV uses the CIA <strong>commuted-value basis (§3500)</strong> and the
            <strong>50/50 retirement-age rule</strong>. Enter the CIA commuted-value interest rates for
            the <strong>separation-date month</strong> (published monthly by the CIA); the select period
            is typically <strong>10 years</strong>. Mortality is CPM2014 + generational CPM-B; the
            member's sex comes from the client record. Valued as at {{ separationDate ? fmtDate(separationDate) : 'the separation date' }}.
          </p>
          <p class="sub" v-else>
            Discount rates are <strong>nominal</strong> (per CIA Standards of Practice). Mortality is
            CPM2014 with the full generational CPM-B improvement scale; the member's sex comes from the client record.
          </p>
          <div class="field val-date-field" v-if="!isOntario">
            <label>Valuation Date</label>
            <date-field v-model="valuationDate" :disabled="readOnly" />
            <span class="hint">
              Defaults to today (a fresh valuation). Set a specific or agreed date — or the date of an
              existing report — to value as at that date. Make sure the pension figures you enter match it.
            </span>
          </div>
          <div class="assumptions-grid">
            <div class="field">
              <label>Discount Rate, years 1–{{ selectYears }} — {{ discountRate.toFixed(1) }}%</label>
              <input type="range" min="0" max="8" step="0.1" v-model.number="discountRate" class="slider" />
            </div>
            <div class="field">
              <label>Discount Rate, after {{ selectYears }} yrs — {{ discountRate2.toFixed(1) }}%</label>
              <input type="range" min="0" max="8" step="0.1" v-model.number="discountRate2" class="slider" />
            </div>
            <div class="field" v-if="pensionIndexed">
              <label>Indexation, years 1–{{ selectYears }} — {{ inflationRate.toFixed(1) }}%</label>
              <input type="range" min="0" max="5" step="0.1" v-model.number="inflationRate" class="slider" />
            </div>
            <div class="field" v-if="pensionIndexed">
              <label>Indexation, after {{ selectYears }} yrs — {{ inflationRate2.toFixed(1) }}%</label>
              <input type="range" min="0" max="5" step="0.1" v-model.number="inflationRate2" class="slider" />
              <span class="hint">CIA reports often split CPI (e.g. 1.33% then 1.75%). Leave equal to use one rate.</span>
            </div>
            <div class="field">
              <label>Spouse's Share of Marital Portion — {{ spouseShare }}%</label>
              <input type="range" min="0" max="100" step="1" v-model.number="spouseShare" class="slider" />
            </div>
            <div class="field" v-if="!isInPay">
              <label>Annual Benefit Accrual Rate — {{ benefitAccrualRate.toFixed(1) }}%</label>
              <input type="range" min="1" max="3" step="0.1" v-model.number="benefitAccrualRate" class="slider" />
            </div>
            <div class="field" v-if="isInPay && !isOntario">
              <label>Estimated Tax Rate — {{ inPayTaxRate }}%</label>
              <input type="range" min="0" max="50" step="1" v-model.number="inPayTaxRate" class="slider" />
            </div>
            <div class="field" v-if="isOntario">
              <label>Estimated Tax Rate (after-tax estimate) — {{ ontarioTaxRate }}%</label>
              <input type="range" min="0" max="50" step="1" v-model.number="ontarioTaxRate" class="slider" />
              <span class="hint">Used only for the after-tax comparison figure — not part of the official FL-4.</span>
            </div>
          </div>
          <div class="row-3 assumptions-extra">
            <div class="field" v-if="!isInPay && !isOntario && pensionSource === 'formula'">
              <label>Max Pensionable Service (yrs)</label>
              <input type="number" min="0" max="45" step="1" v-model.number="maxServiceYears" />
              <span class="hint">Most BC plans cap service at 35. Projected service won't exceed this.</span>
            </div>
            <div class="field">
              <label>Select Period (yrs)</label>
              <input type="number" min="1" max="40" step="1" v-model.number="selectYears" />
            </div>
            <div class="field">
              <label>Guarantee Period (yrs)</label>
              <input type="number" min="0" max="20" step="1" v-model.number="guaranteeYears" />
            </div>
            <div class="field">
              <label>Pension Indexed?</label>
              <label class="checkbox-line"><input type="checkbox" v-model="pensionIndexed" /> Indexed to inflation</label>
              <span class="hint">
                Most provincial public plans <strong>are</strong> indexed (PSPP, MPP, Teachers',
                etc). Uncheck only for a non-indexed corporate plan; if unsure, check as indexed.
              </span>
            </div>
          </div>
          <div class="field assumptions-extra">
            <label>Mortality Table (CPM2014 sector)</label>
            <select v-model="mortalitySector" class="sector-select">
              <option value="composite">Composite (blended — default)</option>
              <option value="public">Public sector — government (federal / provincial)</option>
              <option value="private">Private sector — corporate / non-government</option>
            </select>
          </div>
          </div>
        </section>

      </fieldset>

      <!-- Generate action — spans full width below the entry-page form -->
      <div v-if="view === 'entry'" class="entry-actions">
        <button class="btn-primary btn-generate" @click="generate">Generate Report →</button>
        <p v-if="errorMsg" ref="errorBox" class="error-box">{{ errorMsg }}</p>
      </div>

      <!-- ═════════════════════════════════════════════════════════
           RESULTS COLUMN — only on the report page
      ═════════════════════════════════════════════════════════ -->
      <div v-if="view === 'report'" class="pc-results">

        <template v-if="calculated">
          <!-- Summary of data — mirrors all of the form inputs for easy
               reference (the editable form lives behind "Back to form"). -->
          <section class="step">
            <h2>Summary of Data</h2>
            <div class="summary-cols">
              <div class="summary-block">
                <h4>Client</h4>
                <p><strong>Name:</strong> {{ clientName || 'N/A' }}</p>
                <p><strong>Gender:</strong> {{ gender || 'N/A' }}</p>
                <p><strong>Date of Birth:</strong> {{ fmtDate(birthdate) }}</p>
                <p><strong>Current Age:</strong> {{ currentAge ? fmt2(currentAge) + ' yrs' : 'N/A' }}</p>
              </div>
              <div class="summary-block">
                <h4>Employment</h4>
                <!-- DIVII PATCH: which of the matter's pensions this valuation is for. -->
                <p v-if="pensionProp && pensionProp.name"><strong>Pension:</strong> {{ pensionProp.name }}</p>
                <p><strong>Plan Jurisdiction:</strong> {{ planJurisdiction || 'N/A' }}<span v-if="isOntario"> (Ontario FLV)</span></p>
                <p><strong>Pension Status:</strong> {{ pensionStatusLabel }}</p>
                <p><strong>Date of Hire:</strong> {{ fmtDate(hireDate) }}</p>
                <!-- DIVII PATCH: former member: show the leaving date; service below is frozen there. -->
                <p v-if="isDeferred"><strong>Employment End Date:</strong> {{ fmtDate(terminationDate) }}</p>
                <template v-if="isInPay">
                  <p><strong>Retirement Date:</strong> {{ fmtDate(retirementDate) }}</p>
                  <p><strong>Years of Service:</strong> {{ fmt2(totalServiceYears) }} yrs</p>
                  <p><strong>Annual Pension in Pay:</strong> {{ fmtMoney(annualPensionInPay) }}</p>
                </template>
                <template v-else>
                  <p><strong>Years of Service:</strong> {{ fmt2(yearsOfService) }} yrs</p>
                  <p><strong>Valuation Basis:</strong> {{ isDeferred ? 'Former member (service to employment end date)' : valuationBasis === 'terminate' ? 'Termination (service to date)' : 'Continue to retirement (projected)' }}</p>
                  <template v-if="pensionSource === 'formula'">
                    <p><strong>{{ isDeferred ? 'Annual Salary at Employment End' : 'Current Annual Salary' }}:</strong> {{ fmtMoney(currentSalary) }}</p>
                    <p v-if="!isDeferred"><strong>Expected Salary Growth:</strong> {{ expectedSalaryGrowth.toFixed(1) }}%</p>
                  </template>
                  <p v-else-if="!isDeferred"><strong>Wage Growth to Retirement:</strong> {{ expectedSalaryGrowth.toFixed(1) }}%</p>
                </template>
              </div>
              <div class="summary-block">
                <h4>Relationship</h4>
                <p><strong>Marriage / Cohabitation:</strong> {{ fmtDate(cohabitationDate) }}</p>
                <p><strong>Separation:</strong> {{ fmtDate(separationDate) }}</p>
                <p><strong>Years in Relationship:</strong> {{ fmt2(yearsMarried) }} yrs</p>
                <p><strong>Proportion of Service:</strong> {{ fmtPct(isOntario ? ontarioRatio : proportionDuringRelationship) }}</p>
              </div>
            </div>
            <p class="summary-valuation">
              <strong>Date of Valuation:</strong>
              {{ isOntario ? fmtDate(separationDate) + ' (Family Law Valuation Date)' : fmtDate(valuationDate) }}
            </p>
          </section>

          <!-- Timeline — relationship vs pension-membership; shaded overlap is
               the family-property service we value. Pension bar ends at the
               basis denominator, so the box width = the % reported. -->
          <section class="step" v-if="timeline">
            <h2>Relationship &amp; Pension Timeline</h2>
            <svg class="tl-svg" viewBox="0 0 1000 200" preserveAspectRatio="xMidYMid meet" role="img"
                 :aria-label="'Family property is ' + fmtPct(timeline.ratio) + ' of the pension'">
              <!-- date marker lines + labels -->
              <g v-for="(m, i) in timeline.markers" :key="'m' + i">
                <line class="tl-tick" :x1="m.x" :x2="m.x" y1="68" y2="168" />
                <text class="tl-tick-label" :x="m.x" :y="m.level === 0 ? 14 : 46" text-anchor="middle">
                  <tspan :x="m.x" dy="0">{{ m.label.split(' ').slice(0, 2).join(' ') }}</tspan>
                  <tspan :x="m.x" dy="14">{{ m.label.split(' ').slice(2).join(' ') }}</tspan>
                </text>
              </g>
              <!-- overlap (family property) -->
              <rect v-if="timeline.overlap" class="tl-overlap"
                    :x="timeline.overlap.x1" y="90" :width="timeline.overlap.x2 - timeline.overlap.x1" height="60" />
              <text v-if="timeline.overlap" class="tl-fp"
                    :x="(timeline.overlap.x1 + timeline.overlap.x2) / 2" y="124" text-anchor="middle">Family Property</text>
              <!-- pension membership bar -->
              <line class="tl-pension" :x1="timeline.pension.x1" :x2="timeline.pension.x2" y1="90" y2="90" />
              <circle class="tl-pension-dot" :cx="timeline.pension.x1" cy="90" r="6" />
              <path v-if="timeline.pension.continues" class="tl-arrow"
                    :d="'M' + (timeline.pension.x2 - 1) + ',84 L' + (timeline.pension.x2 + 12) + ',90 L' + (timeline.pension.x2 - 1) + ',96 Z'" />
              <circle v-else class="tl-pension-dot" :cx="timeline.pension.x2" cy="90" r="6" />
              <text class="tl-bar-label tl-teal" :x="(timeline.pension.x1 + timeline.pension.x2) / 2" y="83" text-anchor="middle">Pension membership — {{ fmt1(timeline.pension.years) }} yrs</text>
              <!-- relationship bar -->
              <line class="tl-relationship" :x1="timeline.relationship.x1" :x2="timeline.relationship.x2" y1="150" y2="150" />
              <circle class="tl-rel-dot" :cx="timeline.relationship.x1" cy="150" r="6" />
              <circle class="tl-rel-dot" :cx="timeline.relationship.x2" cy="150" r="6" />
              <text class="tl-bar-label tl-gold" :x="(timeline.relationship.x1 + timeline.relationship.x2) / 2" y="168" text-anchor="middle">Relationship — {{ fmt1(timeline.relationship.years) }} yrs</text>
            </svg>
            <p class="tl-caption" v-if="timeline.overlap">
              <strong>{{ fmt1(timeline.overlap.years) }} of {{ fmt1(timeline.pension.years) }} years</strong>
              of pension membership were earned during the relationship —
              <strong>{{ fmtPct(timeline.ratio) }}</strong> of the pension is family property (the shaded portion).
            </p>
            <p class="tl-caption" v-else>
              The relationship and pension membership don't overlap, so no part of the pension is family property.
            </p>
          </section>

          <!-- ── Ontario FLV report ──────────────────────────────── -->
          <section class="step" v-if="isOntario && ontarioResults">
            <h2>Estimated Family Law Value (Ontario)</h2>
            <p class="sub">
              Prescribed Family Law Value (FSRA Form {{ ontarioFormCode }}) estimated as at the
              separation date on the CIA commuted-value basis (§3500). The FL-4 value is <strong>pre-tax</strong>;
              an after-tax estimate is shown below for comparing against post-tax assets.
            </p>
            <table class="flv-table">
              <tbody>
                <tr>
                  <td>Pension plan jurisdiction</td>
                  <td>{{ planJurisdiction }} (Ontario regime)</td>
                </tr>
                <tr>
                  <td>Family Law Valuation Date</td>
                  <td>{{ fmtDate(separationDate) }} (age {{ fmt2(ageAtFLVD) }})</td>
                </tr>
                <template v-if="!isInPay">
                  <tr>
                    <td>Highest-value commencement age</td>
                    <td>{{ ontarioResults.highestValueAge }} — {{ fmtMoney(ontarioResults.highestValue) }}</td>
                  </tr>
                  <tr v-if="ontarioResults.subsidizedEarly">
                    <td>Earliest-unreduced age (50/50 rule)</td>
                    <td>{{ ontarioResults.earliestUnreducedAge }} — {{ fmtMoney(ontarioResults.earliestUnreducedValue) }}</td>
                  </tr>
                  <tr class="flv-note-row" v-if="ontarioResults.subsidizedEarly">
                    <td colspan="2">FLV = 50% × highest-value + 50% × earliest-unreduced (CIA 50/50 rule).</td>
                  </tr>
                  <tr class="flv-note-row" v-else>
                    <td colspan="2">FLV = value at the highest-value commencement age (member not flagged as eligible for a subsidized unreduced early pension).</td>
                  </tr>
                </template>
                <tr class="flv-subtotal">
                  <td>Preliminary value (full FLV)</td>
                  <td>{{ fmtMoney(ontarioResults.flv) }}</td>
                </tr>
                <tr>
                  <td>Plan membership during relationship</td>
                  <td>{{ fmt2(relServiceToFLVD) }} of {{ fmt2(serviceToFLVD) }} yrs to the FLVD</td>
                </tr>
                <!-- DIVII PATCH -->
                <tr v-if="ontarioDeferredProration < 1">
                  <td>Entered figures prorated to the FLVD (employment ended after separation)</td>
                  <td>{{ fmt2(serviceToFLVD) }} of {{ fmt2(ontarioTotalService) }} yrs, {{ fmtPct(ontarioDeferredProration) }}</td>
                </tr>
                <tr>
                  <td>Proportion (imputed-value ratio)</td>
                  <td>{{ fmtPct(ontarioResults.ratio) }}</td>
                </tr>
                <tr class="row-highlight">
                  <td>Imputed Value (family-law portion)</td>
                  <td>{{ fmtMoney(ontarioResults.imputedValue) }}</td>
                </tr>
                <tr>
                  <td>Spouse's maximum share — 50% of imputed value (by transfer or offset)</td>
                  <td>{{ fmtMoney(ontarioResults.statutoryCap) }}</td>
                </tr>
                <tr class="row-highlight">
                  <td>Spouse's {{ Math.min(spouseShare, 50) }}% share (pre-tax)</td>
                  <td>{{ fmtMoney(ontarioResults.spouseTransfer) }}</td>
                </tr>
                <tr class="flv-note-row">
                  <td colspan="2">After-tax estimates below ({{ ontarioTaxRate }}% assumed rate) — for comparing against post-tax assets (e.g. home equity). Not part of the official FL-4.</td>
                </tr>
                <tr>
                  <td>Imputed value — after-tax estimate</td>
                  <td>{{ fmtMoney(ontarioResults.imputedValuePostTax) }}</td>
                </tr>
                <tr class="row-highlight">
                  <td>Spouse's {{ Math.min(spouseShare, 50) }}% share — after-tax estimate</td>
                  <td>{{ fmtMoney(ontarioResults.spouseTransferPostTax) }}</td>
                </tr>
              </tbody>
            </table>
            <p class="flv-caveat">
              <strong>Preliminary estimate only.</strong> The official Family Law Value is calculated by
              the plan administrator and issued on Form FL-4B/FL-4E under O. Reg. 287/11. The spouse's share
              from the pension is capped at 50% of the imputed value, and may be settled either by a transfer
              out of the pension or by offsetting against other family assets. The after-tax figures are an
              estimate (pre-tax value less an assumed retirement tax rate) to compare against post-tax
              assets — the FL-4 itself is pre-tax; tax treatment in Ontario is handled separately.
            </p>
          </section>

          <!-- DIVII PATCH: the Ontario "Record in Agreement" opt-in checkbox moved into
               the shared Record in Agreement section below (pre/post-tax choice +
               Record button), so both regimes record the same way. -->

          <!-- Valuation results — grouped into tables of up to 3 scenarios,
               each with its pie charts directly under the scenario columns. -->
          <section class="step" v-if="!isOntario">
            <h2>Pension Valuation Results</h2>
            <p class="sub">Each scenario's pie chart (marital vs. non-marital portion) appears directly under its column.</p>
            <div v-for="(group, gi) in scenarioGroups" :key="'grp' + gi" class="table-scroll result-group">
              <table class="results-table">
                <thead>
                  <tr>
                    <th class="metric-col">Metric</th>
                    <th v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">
                      <template v-if="item.r">Scenario {{ item.i + 1 }}</template>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="row-desc">
                    <td>Description</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? item.r.shortLabel : '' }}</td>
                  </tr>
                  <tr>
                    <td>Pension Start Date</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtDate(item.r.pensionStartDate) : '' }}</td>
                  </tr>
                  <tr>
                    <td>Total Pension Value</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.total) : '' }}</td>
                  </tr>
                  <tr>
                    <td>Marital Portion Value</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.marital) : '' }}</td>
                  </tr>
                  <tr>
                    <td>Marital Portion (%)</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtPct(item.r.maritalPercentage) : '' }}</td>
                  </tr>
                  <tr class="row-highlight">
                    <td>Spouse's {{ spouseShare }}% Pre-Tax Share</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.spousePretax) : '' }}</td>
                  </tr>
                  <tr>
                    <td>Estimated Tax Rate</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtPct(item.r.taxRate) : '' }}</td>
                  </tr>
                  <tr>
                    <td>Post-Tax Marital Portion</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.maritalPosttax) : '' }}</td>
                  </tr>
                  <tr class="row-highlight">
                    <td>Spouse's {{ spouseShare }}% Post-Tax Share</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.spousePosttax) : '' }}</td>
                  </tr>
                  <tr class="chart-row">
                    <td class="chart-row-label">Marital vs.<br>Non-Marital</td>
                    <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">
                      <div v-if="item.r" class="chart-cell"><canvas :ref="'chart' + item.i"></canvas></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <!-- Record in Agreement — pick a scenario, then the pre-tax or post-tax
               figure, and record it against the matter's pension. -->
          <!-- DIVII PATCH: shared for BC scenarios and the Ontario FLV; the figure is
               selectable and a Record button hands it to the host (PensionCalculatorTab),
               which writes the pension's value in the builder. -->
          <section class="step" v-if="(isOntario && ontarioResults) || (!isOntario && results.length)">
            <h2>Record in Agreement</h2>
            <p class="sub" v-if="isOntario">Record this Family Law Value in your overall calculations. Choose the pre-tax or post-tax imputed value; it is recorded as the pension's value in the builder and flows into the Property Schedule.</p>
            <p class="sub" v-else>Choose the scenario to use in your overall calculations, then the pre-tax or post-tax marital portion. It is recorded as the pension's value in the builder and flows into the Property Schedule.</p>
            <div class="field record-field" v-if="!isOntario">
              <label>Scenario to record</label>
              <select v-model.number="selectedScenarioIdx" class="sector-select" :disabled="readOnly">
                <option :value="null">— None selected —</option>
                <option v-for="(r, i) in results" :key="i" :value="i">{{ r.fullLabel }}</option>
              </select>
            </div>
            <div v-if="selectedResult" class="record-readout record-readout--select" role="radiogroup" aria-label="Value to record">
              <button
                v-for="row in recordRows" :key="row.basis" type="button"
                class="record-row record-row--option" :class="{ selected: recordTaxBasis === row.basis }"
                role="radio" :aria-checked="recordTaxBasis === row.basis ? 'true' : 'false'"
                :disabled="readOnly"
                @click="recordTaxBasis = row.basis"
              >
                <span class="record-radio" aria-hidden="true"></span>
                <span class="record-label">{{ row.label }}</span>
                <strong>{{ fmtMoney(row.amount) }}</strong>
              </button>
            </div>
            <div class="record-explain" v-if="selectedResult">
              <p><strong>Pre-tax</strong> is the family-property share of the pension before income tax on the eventual pension payments. Compare it with other assets that are still taxable when drawn, such as RRSPs.</p>
              <p><strong>Post-tax</strong> is the same share after the estimated tax rate. Compare it with assets already stated after tax, such as the family residence, bank accounts and vehicles.</p>
              <p>Your choice also sets the pension's "before-tax or after-tax value" answer in the builder, which places it in the matching column of the Property Schedule. The recorded value stays editable in the Pensions section.</p>
            </div>
            <div class="record-actions" v-if="selectedResult">
              <p class="record-target" v-if="pensionProp">
                Records to <strong>{{ pensionProp.name || 'the selected pension' }}</strong><template v-if="pensionProp.ownerName"> (owned by {{ pensionProp.ownerName }})</template>.
                <template v-if="pensionHasValue"> It currently holds <strong>{{ fmtMoney(Number(pensionProp.value)) }}</strong>; you will be asked before it is replaced.</template>
              </p>
              <p class="record-warn" v-if="recordOwnerMismatch">
                This valuation is for {{ clientName }}, but the builder lists {{ pensionProp.ownerName }} as the owner of {{ pensionProp.name || 'this pension' }}. You can still record it; check the owner in the Pensions section.
              </p>
              <button type="button" class="btn-primary" :disabled="!!recordDisabledReason" @click="recordInAgreement">Record in agreement</button>
              <span class="hint" v-if="recordDisabledReason">{{ recordDisabledReason }}</span>
              <p class="record-note" v-if="recordedProp">
                Recorded to {{ recordedProp.pensionName || (pensionProp && pensionProp.name) || 'the pension' }} as
                <strong>{{ fmtMoney(recordedProp.amount) }}</strong> ({{ recordedProp.basis === 'pre' ? 'pre-tax' : 'post-tax' }}<template v-if="recordedProp.scenario">, {{ recordedProp.scenario }}</template>)<template v-if="recordedProp.at"> on {{ fmtDate(recordedProp.at) }}</template>.
              </p>
            </div>
          </section>

          <!-- Report Details — full methodology/assumptions appendix (collapsible) -->
          <section class="step">
            <h2 class="step-toggle" @click="reportDetailsOpen = !reportDetailsOpen"
                role="button" tabindex="0" @keydown.enter="reportDetailsOpen = !reportDetailsOpen"
                :aria-expanded="reportDetailsOpen ? 'true' : 'false'">
              <span>Report Details</span>
              <span class="step-chevron" :class="{ open: reportDetailsOpen }">▸</span>
            </h2>
            <p class="assump-summary" v-if="!reportDetailsOpen"
               @click="reportDetailsOpen = true" role="button" tabindex="0"
               @keydown.enter="reportDetailsOpen = true">
              Methodology, mortality table &amp; assumptions — <span class="assump-adjust">click to view</span>
            </p>
            <div v-show="reportDetailsOpen">
              <label class="checkbox-line pdf-optin">
                <input type="checkbox" v-model="includeDetailsInPdf" /> Add this section to the PDF
              </label>
              <table class="details-table">
                <tbody>
                  <tr v-for="(row, i) in reportDetailRows" :key="i">
                    <td>{{ row[0] }}</td><td>{{ row[1] }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <div class="results-actions">
            <button class="btn-primary" @click="printReport">🖨 Print / Save as PDF</button>
          </div>
        </template>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════
         PRINT WORKSHEET — hidden on screen, revealed for print/PDF.
    ═══════════════════════════════════════════════════════════ -->
    <div class="print-worksheet" aria-hidden="true" v-if="calculated">
      <div class="pw-header">
        <img class="pw-logo" src="@/assets/Divii-Logo-Primary-Colour.png" alt="Divii" />
        <h1>{{ isOntario ? 'Family Law Value Estimate (Ontario)' : 'Pension Value Estimate' }}</h1>
      </div>
      <p class="pw-intro">
        This estimates the value of a defined-benefit pension earned during marriage or cohabitation,
        so you can see whether it's significant enough to be worth offsetting against other family
        assets — for example, so one spouse can keep the home. If the value looks material, obtain a
        formal actuarial valuation before settling.
      </p>
      <div class="pw-disclaimer" v-if="isOntario">
        Preliminary estimate only. The official Family Law Value is calculated by the plan
        administrator and issued on Form FL-4B/FL-4E under O. Reg. 287/11. The spouse's share from
        the pension is capped at 50% of the imputed value, and may be settled either by a transfer
        out of the pension or by offsetting against other family assets.
      </div>
      <div class="pw-disclaimer" v-else>
        IMPORTANT: This pension value estimate is provided for informational purposes only and
        does not constitute legal, financial, or actuarial advice. The calculations are based on
        the information provided and assumptions that may not reflect your specific circumstances.
        For a detailed and legally recognized valuation, consult a qualified actuary. For federal
        pensions (PSSA, Canadian Forces, RCMP) divided under the Pension Benefits Division Act, the
        plan administrator provides the official division value at no cost.
      </div>

      <div class="pw-cols">
        <div class="pw-col">
          <h2>{{ clientName || 'Client' }}'s Information</h2>
          <p><strong>Date of Birth (Age {{ fmt2(currentAge) }}):</strong> {{ fmtLongDate(birthdate) }}</p>
          <p><strong>Gender:</strong> {{ gender }}</p>
          <!-- DIVII PATCH -->
          <p v-if="pensionProp && pensionProp.name"><strong>Pension Valued:</strong> {{ pensionProp.name }}</p>
          <p><strong>Date of Cohabitation / Marriage:</strong> {{ fmtLongDate(cohabitationDate) }}</p>
          <p><strong>Date of Separation:</strong> {{ fmtLongDate(separationDate) }}</p>
          <template v-if="isInPay">
            <p><strong>Pension Status:</strong> Retired — in pay (from {{ fmtLongDate(retirementDate) }})</p>
            <p><strong>Years of Service:</strong> {{ fmt2(totalServiceYears) }} years</p>
            <p><strong>Annual Pension in Pay:</strong> {{ fmtMoney(annualPensionInPay) }}</p>
          </template>
          <!-- DIVII PATCH: former member block for the print worksheet. -->
          <template v-else-if="isDeferred">
            <p><strong>Pension Status:</strong> Former member, deferred pension (left {{ fmtLongDate(terminationDate) }})</p>
            <p><strong>Years of Service:</strong> {{ fmt2(yearsOfService) }} years</p>
            <p v-if="pensionSource === 'formula'"><strong>Annual Salary at Employment End:</strong> {{ fmtMoney(currentSalary) }}</p>
          </template>
          <template v-else>
            <p><strong>Years of Service:</strong> {{ fmt2(yearsOfService) }} years</p>
            <p><strong>Current Annual Salary:</strong> {{ fmtMoney(currentSalary) }}</p>
          </template>
          <p v-if="isOntario"><strong>Plan Jurisdiction:</strong> {{ planJurisdiction }} (Ontario FLV)</p>
          <p v-if="isOntario"><strong>Family Law Valuation Date:</strong> {{ fmtLongDate(separationDate) }}</p>
          <p v-else><strong>Date of Valuation:</strong> {{ fmtLongDate(valuationDate) }}</p>
        </div>
        <div class="pw-col">
          <h2>{{ isOntario ? 'Commuted Value Basis (CIA §3500)' : 'Calculation Assumptions' }}</h2>
          <p v-if="!isInPay && !isDeferred"><strong>Expected Salary Growth:</strong> {{ expectedSalaryGrowth.toFixed(1) }}%</p>
          <p><strong>Discount Rate (1–{{ selectYears }} yrs):</strong> {{ discountRate.toFixed(1) }}%</p>
          <p><strong>Discount Rate ({{ selectYears }}+ yrs):</strong> {{ discountRate2.toFixed(1) }}%</p>
          <p><strong>Indexation:</strong> {{ pensionIndexed ? inflationRate.toFixed(1) + '%' : 'Not indexed' }}</p>
          <p><strong>Guarantee Period:</strong> {{ guaranteeYears }} years</p>
          <p v-if="!isInPay"><strong>Benefit Accrual Rate:</strong> {{ benefitAccrualRate.toFixed(1) }}%</p>
          <p v-if="isInPay"><strong>Estimated Tax Rate:</strong> {{ inPayTaxRate }}%</p>
          <p><strong>Mortality:</strong> {{ mortalityLabel }}</p>
          <p><strong>Spouse's Share of Marital Portion:</strong> {{ spouseShare }}%</p>
        </div>
      </div>

      <!-- Ontario FLV print results -->
      <template v-if="isOntario && ontarioResults">
        <h2>Estimated Family Law Value (pre-tax)</h2>
        <table class="pw-table pw-flv-table">
          <tbody>
            <tr><td>Pension plan jurisdiction</td><td>{{ planJurisdiction }} (Ontario regime)</td></tr>
            <tr><td>Family Law Valuation Date</td><td>{{ fmtLongDate(separationDate) }} (age {{ fmt2(ageAtFLVD) }})</td></tr>
            <template v-if="!isInPay">
              <tr><td>Highest-value commencement age</td><td>{{ ontarioResults.highestValueAge }} — {{ fmtMoney(ontarioResults.highestValue) }}</td></tr>
              <tr v-if="ontarioResults.subsidizedEarly"><td>Earliest-unreduced age (50/50 rule)</td><td>{{ ontarioResults.earliestUnreducedAge }} — {{ fmtMoney(ontarioResults.earliestUnreducedValue) }}</td></tr>
            </template>
            <tr><td><strong>Preliminary value (full FLV)</strong></td><td><strong>{{ fmtMoney(ontarioResults.flv) }}</strong></td></tr>
            <tr><td>Plan membership during relationship</td><td>{{ fmt2(relServiceToFLVD) }} of {{ fmt2(serviceToFLVD) }} yrs</td></tr>
            <!-- DIVII PATCH -->
            <tr v-if="ontarioDeferredProration < 1"><td>Entered figures prorated to the FLVD</td><td>{{ fmt2(serviceToFLVD) }} of {{ fmt2(ontarioTotalService) }} yrs ({{ fmtPct(ontarioDeferredProration) }})</td></tr>
            <tr><td>Proportion (imputed-value ratio)</td><td>{{ fmtPct(ontarioResults.ratio) }}</td></tr>
            <tr><td><strong>Imputed Value (family-law portion)</strong></td><td><strong>{{ fmtMoney(ontarioResults.imputedValue) }}</strong></td></tr>
            <tr><td>Spouse's maximum share — 50% of imputed value (by transfer or offset)</td><td>{{ fmtMoney(ontarioResults.statutoryCap) }}</td></tr>
            <tr><td><strong>Spouse's {{ Math.min(spouseShare, 50) }}% share (pre-tax)</strong></td><td><strong>{{ fmtMoney(ontarioResults.spouseTransfer) }}</strong></td></tr>
            <tr><td>Imputed value — after-tax estimate ({{ ontarioTaxRate }}%)</td><td>{{ fmtMoney(ontarioResults.imputedValuePostTax) }}</td></tr>
            <tr><td><strong>Spouse's {{ Math.min(spouseShare, 50) }}% share — after-tax estimate</strong></td><td><strong>{{ fmtMoney(ontarioResults.spouseTransferPostTax) }}</strong></td></tr>
          </tbody>
        </table>
        <p class="pw-disclaimer">The after-tax figures are an estimate (pre-tax value less an assumed {{ ontarioTaxRate }}% retirement tax rate) for comparing against post-tax assets. The official FL-4 value is pre-tax.</p>
      </template>

      <!-- B.C. setoff print results (scenarioGroups is empty in Ontario mode) -->
      <h2 v-if="!isOntario">Pension Value Results</h2>
      <div v-for="(group, gi) in scenarioGroups" :key="'pwg' + gi" class="pw-group">
        <table class="pw-table">
          <thead>
            <tr>
              <th>Metric</th>
              <th v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? item.r.fullLabel : '' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Pension Start Date</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtDate(item.r.pensionStartDate) : '' }}</td></tr>
            <tr><td>Total Pension Value</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.total) : '' }}</td></tr>
            <tr><td>Marital Portion Value</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.marital) : '' }}</td></tr>
            <tr><td>Marital Portion (%)</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtPct(item.r.maritalPercentage) : '' }}</td></tr>
            <tr><td>Spouse's {{ spouseShare }}% Pre-Tax Share</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.spousePretax) : '' }}</td></tr>
            <tr><td>Estimated Tax Rate</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtPct(item.r.taxRate) : '' }}</td></tr>
            <tr><td>Post-Tax Marital Portion</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.maritalPosttax) : '' }}</td></tr>
            <tr><td>Spouse's {{ spouseShare }}% Post-Tax Share</td><td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">{{ item.r ? fmtMoney(item.r.spousePosttax) : '' }}</td></tr>
            <tr class="pw-chart-row">
              <td></td>
              <td v-for="(item, k) in group" :key="k" :class="{ 'col-empty': !item.r }">
                <img v-if="item.r && chartImages[item.i]" :src="chartImages[item.i]" class="pw-chart-img" alt="" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Report Details — only printed if the user opted in on-screen -->
      <template v-if="includeDetailsInPdf">
        <h2>Report Details</h2>
        <table class="pw-table pw-details-table">
          <tbody>
            <tr v-for="(row, i) in reportDetailRows" :key="i">
              <td>{{ row[0] }}</td><td>{{ row[1] }}</td>
            </tr>
          </tbody>
        </table>
      </template>

      <p class="pw-footer">Generated by the Divii Pension Value Estimator · {{ fmtLongDate(today) }}</p>
    </div>

  </div>
</template>

<script>
import Chart from 'chart.js/auto'
// Same date picker the rest of the Divii app uses (see Form/DateInput.vue).
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import {
  calculatePensionValue,
  calculateInPayValue,
  valuePensionStream,
  valueOntarioFLV,
  calculateAge,
  calculateYearsBetween,
  toDate,
} from './pension.js'

// Wraps the app-wide vue2-datepicker so the calculator's dates use the same
// picker as the rest of Divii. Displays MM-DD-YYYY (the app's convention) while
// keeping the value as ISO yyyy-mm-dd, which the engine and Divii props expect.
// Replaces an earlier custom text field that only accepted US mm/dd/yyyy order
// and silently reverted other input on blur (dates appeared to "disappear").
const DateField = {
  name: 'DateField',
  props: {
    value: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
  },
  // Render function (Vite build is runtime-only — no string-template compiler).
  render(h) {
    return h(DatePicker, {
      props: {
        value: this.value || null,
        valueType: 'YYYY-MM-DD',
        format: 'MM-DD-YYYY',
        type: 'date',
        placeholder: 'mm-dd-yyyy',
        disabled: this.disabled,
      },
      style: { width: '100%' },
      on: {
        input: (val) => { this.$emit('input', val || '') },
      },
    })
  },
}

export default {
  name: 'PensionCalculator',
  components: { DateField },

  props: {
    // Parent app (Divii) provides the client roster. The lawyer picks one from
    // the dropdown and that client's personal details (DOB, gender) load in.
    // Each item: { name: string, dob: string (ISO yyyy-mm-dd), gender: 'Male' | 'Female' }
    clientsProp: { type: Array, default: () => [] },
    // Date of hire is NOT held in Divii — it's entered in the calculator.
    // salaryProp/relationship dates may be seeded from Divii when available.
    hireDateProp: { type: String, default: null },
    salaryProp: { type: Number, default: null },
    cohabitationDateProp: { type: String, default: null },
    separationDateProp: { type: String, default: null },
    // Divii-sourced. Flags that a pension valuation may not be applicable.
    relationshipUnderTwoYearsProp: { type: Boolean, default: false },
    // The matter's province of residence (Divii-sourced). Used only as the
    // SEED for the pension-plan jurisdiction — the valuation regime follows
    // where the PLAN is registered, which the lawyer can override below.
    provinceProp: { type: String, default: null },
    // Opaque saved state previously emitted via `update-state`, stored on the
    // Matter record. Restores all inputs when the panel reopens.
    initialState: { type: Object, default: null },
    // DIVII FIX — Divii's User Role page can withhold Edit on this tool.
    readOnly: { type: Boolean, default: false },
    // DIVII PATCH: the matter's pension this valuation is for (chosen in the host
    // tab): { id, name, ownerName, value, orderOfTax } or null when none is chosen.
    pensionProp: { type: Object, default: null },
    // DIVII PATCH: false for read-only roles; the report still shows, recording is disabled.
    canRecordProp: { type: Boolean, default: true },
    // DIVII PATCH: what the host last recorded for this pension:
    // { amount, basis: 'pre' | 'post', at, scenario, pensionName } or null.
    recordedProp: { type: Object, default: null },
    // DIVII PATCH: the matter's pensions for the "Pension being valued" dropdown,
    // as [{ value: index, text }]. null hides the dropdown entirely (standalone use).
    pensionOptionsProp: { type: Array, default: null },
    selectedPensionIndexProp: { type: Number, default: null },
    // DIVII PATCH: index into clientsProp of the chosen pension's owner; used as
    // the default client for a pension that has not been valued yet.
    ownerClientIdxProp: { type: Number, default: null },
  },

  data() {
    const s = this.initialState || {}
    const now = new Date()
    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    return {
      MIN_DATE: '1930-01-01',
      today: now,
      // Valuation date for the B.C. present-value path. Defaults to today (a
      // fresh valuation), but is editable so you can value as of a specific /
      // agreed date or reproduce a dated report. Drives age, service, the
      // deferral, and the valuation year. (Ontario uses the separation date.)
      valuationDate: s.valuationDate ?? todayIso,

      // Index of the chosen client in clientsProp; null = none selected yet.
      // We don't auto-assume a client — the lawyer picks one.
      // DIVII PATCH: a fresh valuation starts on the pension's owner (host-supplied).
      selectedClientIdx: s.selectedClientIdx ?? this.ownerClientIdxProp ?? null,
      hireDate: s.hireDate ?? this.hireDateProp ?? '',
      currentSalary: s.currentSalary ?? this.salaryProp ?? 0,
      expectedSalaryGrowth: s.expectedSalaryGrowth ?? 2.8,

      // Pension status: 'working' = still accruing / not yet retired (projection
      // + scenarios); 'inpay' = already retired and drawing the pension.
      pensionStatus: s.pensionStatus ?? 'working',
      retirementDate: s.retirementDate ?? '',     // actual commencement date (in-pay)
      // DIVII PATCH: 'deferred' = former member; service froze on this date.
      terminationDate: s.terminationDate ?? '',   // employment end date (deferred)
      annualPensionInPay: s.annualPensionInPay ?? 0, // known gross annual pension in pay
      bridgeInPayMonthly: s.bridgeInPayMonthly ?? 0, // monthly bridge to 65 (in-pay)
      inPayTaxRate: s.inPayTaxRate ?? 20,          // estimated tax rate (%) for in-pay

      // Active-member pension source: 'statement' = enter the statement's
      // estimated pension at each retirement age (recommended); 'formula' =
      // derive from salary × accrual × service.
      pensionSource: s.pensionSource ?? 'statement',
      // Valuation basis for an active member:
      //  'terminate' = most likely to terminate employment → value service to
      //     the valuation date (deferred pension, CPI-indexed; marital ratio
      //     over service-to-date). This is the FLA default.
      //  'continue'  = most likely to work to retirement → project service &
      //     salary (wage-escalated; marital ratio over projected service).
      valuationBasis: s.valuationBasis ?? 'terminate',
      // Statement rows: monthly lifetime + bridge from the member's statement,
      // by retirement age (matches the statement's estimate table).
      statementScenarios: s.statementScenarios ?? [
        { age: 60, lifetimeMonthly: 0, bridgeMonthly: 0, taxRate: 20 },
        { age: 65, lifetimeMonthly: 0, bridgeMonthly: 0, taxRate: 22 },
      ],

      // Pension-plan jurisdiction (where the PLAN is registered — drives the
      // valuation regime). Seeded from the matter's province of residence but
      // separately editable: a BC resident can hold an Ontario-registered plan.
      planJurisdiction: s.planJurisdiction ?? this.provinceProp ?? '',
      // Ontario FLV (FL-4B) inputs — deferred "if you terminated on the
      // separation date" figures by commencement age (monthly), the
      // earliest-unreduced age, and subsidized-early eligibility.
      ontarioScenarios: s.ontarioScenarios ?? [
        { age: 60, lifetimeMonthly: 0, bridgeMonthly: 0 },
        { age: 65, lifetimeMonthly: 0, bridgeMonthly: 0 },
      ],
      ontarioEarliestUnreducedAge: s.ontarioEarliestUnreducedAge ?? 65,
      ontarioSubsidized: s.ontarioSubsidized ?? false,
      // DIVII PATCH: former member who left after separation: the entered figures
      // are already dated at the FLVD (skip the service proration).
      ontarioFiguresAtFLVD: s.ontarioFiguresAtFLVD ?? false,
      // Estimated retirement tax rate (%) for the Ontario after-tax estimate
      // (used only to compare against post-tax assets — not part of the FL-4).
      ontarioTaxRate: s.ontarioTaxRate ?? 20,
      // Ontario has a single FLV (nothing to pick between), so recording it back
      // to Divii is a simple opt-in toggle. When true, the imputed-value figures
      // are emitted via `select-result`, mirroring the BC scenario port-back.
      ontarioRecord: s.ontarioRecord ?? false,
      // DIVII PATCH: which figure to record in the agreement: 'pre' | 'post' | null.
      recordTaxBasis: s.recordTaxBasis ?? null,

      // Actuarial assumptions (CIA select-and-ultimate). Rates are NOMINAL;
      // for an indexed pension the indexation rate nets them to a real rate.
      discountRate: s.discountRate ?? 4.0,        // nominal, years 1–N (select)
      discountRate2: s.discountRate2 ?? 5.0,      // nominal, ultimate (after N)
      selectYears: s.selectYears ?? 20,           // length of the select period
      inflationRate: s.inflationRate ?? 2.0,      // indexation rate, years 1–N (select)
      inflationRate2: s.inflationRate2 ?? 2.0,    // indexation rate, ultimate (after N)
      pensionIndexed: s.pensionIndexed ?? true,   // is the pension indexed to inflation?
      guaranteeYears: s.guaranteeYears ?? 10,     // guarantee period (normal form)
      // CPM2014 base table by sector: 'composite' | 'public' | 'private'.
      // Government plans (federal/provincial) → public; corporate → private.
      mortalitySector: s.mortalitySector ?? 'composite',
      benefitAccrualRate: s.benefitAccrualRate ?? 2.0,
      maxServiceYears: s.maxServiceYears ?? 35,    // pensionable-service cap (formula path)
      spouseShare: s.spouseShare ?? 50,

      // Fixed scenarios (1–3) with their default estimated tax rates (%).
      fixedScenarios: s.fixedScenarios ?? [
        { label: 'Stop work now, retire at 60.1', stopWork: true, retirementAge: 60.1, taxRate: 20 },
        { label: 'Continue work, retire at 60.1', stopWork: false, retirementAge: 60.1, taxRate: 20 },
        { label: 'Continue work, retire at 65.1', stopWork: false, retirementAge: 65.1, taxRate: 22 },
      ],
      customScenarios: s.customScenarios ?? [{ age: 62, taxRate: 20 }],

      // Two-page flow: 'entry' = spacious full-width form for first input;
      // 'report' = results with a compact input panel for live adjustments.
      view: 'entry',
      // Calculation Assumptions card collapsed by default (sensible defaults;
      // expand to adjust). UI-only state, not persisted.
      assumptionsOpen: false,
      // Report Details appendix (below the results) collapsed by default.
      reportDetailsOpen: false,
      // Index (into `results`) of the scenario the lawyer selects to record back
      // in Divii. Persisted; its key figures are emitted via `select-result`.
      selectedScenarioIdx: s.selectedScenarioIdx ?? null,
      // Opt-in: include the Report Details appendix in the printed PDF.
      includeDetailsInPdf: s.includeDetailsInPdf ?? false,
      calculated: false,
      errorMsg: '',
      // Base64 PNG of each scenario's pie chart, keyed by scenario index —
      // used to render the charts in the print/PDF worksheet.
      chartImages: [],
    }
  },

  computed: {
    // ── Client (sourced from Divii) ───────────────────────────────
    clients() {
      return this.clientsProp || []
    },
    selectedClient() {
      if (this.selectedClientIdx == null) return null
      return this.clients[this.selectedClientIdx] || null
    },
    clientName() {
      return this.selectedClient ? this.selectedClient.name : ''
    },
    birthdate() {
      return this.selectedClient ? this.selectedClient.dob : ''
    },
    gender() {
      return this.selectedClient ? this.selectedClient.gender : ''
    },

    // ── Jurisdiction routing (drives the valuation regime) ────────
    // Keyed off the pension PLAN's jurisdiction, not the member's residence.
    jurisdiction() {
      return this.planJurisdiction === 'ON' ? 'ontario' : 'setoff'
    },
    isOntario() {
      return this.jurisdiction === 'ontario'
    },
    isFederal() {
      return this.planJurisdiction === 'FED'
    },
    isManitoba() {
      return this.planJurisdiction === 'MB'
    },

    // The B.C. valuation date as a Date (falls back to today if unparseable).
    valuationDateObj() {
      return toDate(this.valuationDate) || this.today
    },
    currentAge() {
      return this.birthdate ? calculateAge(this.birthdate, this.valuationDateObj) : 0
    },
    yearsOfService() {
      if (!this.hireDate) return 0
      // DIVII PATCH: former member: service stopped on the employment end date,
      // not the valuation date. Every downstream denominator (statement
      // terminate basis, salary formula, summary, timeline) reads this value.
      if (this.isDeferred) {
        return this.terminationDate ? calculateYearsBetween(this.hireDate, this.terminationDate) : 0
      }
      return calculateYearsBetween(this.hireDate, this.valuationDateObj)
    },
    isInPay() {
      return this.pensionStatus === 'inpay'
    },
    // DIVII PATCH: former member with a deferred (vested) pension: left the
    // employer, no further accrual, pension not yet in pay.
    isDeferred() {
      return this.pensionStatus === 'deferred'
    },
    // A former member has already terminated, so the statement path is always
    // valued on the termination basis regardless of the radio's saved value.
    isTerminateBasis() {
      return this.isDeferred || this.valuationBasis === 'terminate'
    },
    pensionStatusLabel() {
      if (this.isInPay) return 'Retired — in pay'
      if (this.isDeferred) return 'Former member, deferred pension'
      return 'Still working'
    },
    // FSRA Family Law Value form: FL-4B active member, FL-4D former member
    // with a deferred pension, FL-4E retired member.
    ontarioFormCode() {
      if (this.isInPay) return 'FL-4E'
      if (this.isDeferred) return 'FL-4D'
      return 'FL-4B'
    },
    // Salary-formula fixed scenarios. A former member cannot "continue work",
    // so the three fixed scenarios collapse to one deferred-start per age.
    visibleFixedScenarios() {
      if (!this.isDeferred) return this.fixedScenarios
      const seen = new Set()
      return this.fixedScenarios
        .filter(s => !seen.has(s.retirementAge) && seen.add(s.retirementAge))
        .map(s => ({
          label: `Start pension at ${s.retirementAge}`,
          stopWork: true,
          retirementAge: s.retirementAge,
          taxRate: s.taxRate,
        }))
    },
    // Total pensionable service. For an in-pay member, service stopped at the
    // retirement/commencement date; for a working member it accrues to today.
    totalServiceYears() {
      if (this.isInPay) {
        return this.hireDate && this.retirementDate
          ? calculateYearsBetween(this.hireDate, this.retirementDate)
          : 0
      }
      return this.yearsOfService
    },
    // End of the marriage's overlap with plan membership. In-pay caps it at the
    // retirement date (no service accrues after the pension commences).
    maritalEndDate() {
      if (this.isInPay && this.retirementDate) {
        const sep = toDate(this.separationDate)
        const ret = toDate(this.retirementDate)
        if (sep && ret) return sep < ret ? this.separationDate : this.retirementDate
        return this.separationDate || this.retirementDate
      }
      // DIVII PATCH: former member: no service accrues after the employment end
      // date, so the overlap is capped there (mirrors the in-pay retirement cap).
      if (this.isDeferred && this.terminationDate) {
        const sep = toDate(this.separationDate)
        const term = toDate(this.terminationDate)
        if (sep && term) return sep < term ? this.separationDate : this.terminationDate
        return this.separationDate || this.terminationDate
      }
      return this.separationDate
    },
    // Relationship dates are owned by the Divii matter (the builder). They are
    // read-only here and always reflect the current matter values via the props,
    // so they can never drift from the builder or be shadowed by persisted state.
    cohabitationDate() {
      return this.cohabitationDateProp || ''
    },
    separationDate() {
      return this.separationDateProp || ''
    },
    relationshipUnderTwoYears() {
      return this.relationshipUnderTwoYearsProp
    },
    cohabBeforeHire() {
      const c = toDate(this.cohabitationDate)
      const h = toDate(this.hireDate)
      return !!(c && h && c < h)
    },
    effectiveCohabitationDate() {
      const c = toDate(this.cohabitationDate)
      const h = toDate(this.hireDate)
      if (c && h) return c < h ? this.hireDate : this.cohabitationDate
      return this.cohabitationDate || this.hireDate
    },
    yearsMarried() {
      return calculateYearsBetween(this.effectiveCohabitationDate, this.maritalEndDate)
    },
    proportionDuringRelationship() {
      const denom = this.isInPay ? this.totalServiceYears : this.yearsOfService
      return denom > 0 ? Math.min(this.yearsMarried / denom, 1) : 0
    },
    mortalityLabel() {
      const sec = { composite: 'Composite', public: 'Public sector', private: 'Private sector' }[this.mortalitySector] || 'Composite'
      return `CPM2014 ${sec} + CPM-B (generational)`
    },
    // One-line summary of the active assumptions, shown when the card is collapsed.
    assumptionsSummary() {
      const disc = `${this.discountRate.toFixed(1)}/${this.discountRate2.toFixed(1)}%`
      const idx = this.pensionIndexed
        ? (this.inflationRate === this.inflationRate2
            ? `${this.inflationRate.toFixed(1)}%`
            : `${this.inflationRate.toFixed(1)}/${this.inflationRate2.toFixed(1)}%`)
        : 'not indexed'
      const sec = { composite: 'composite', public: 'public', private: 'private' }[this.mortalitySector] || 'composite'
      if (this.isOntario) {
        return `CV §3500 · disc ${disc} · index ${idx} · CPM2014 ${sec}`
      }
      return `disc ${disc} · index ${idx} · CPM2014 ${sec} · valued ${this.valuationDate ? this.fmtDate(this.valuationDate) : 'today'}`
    },
    // Timeline diagram geometry — plots the real dates and shades the overlap of
    // the relationship and pension-membership periods (= the family-property
    // service). The pension bar ends where our denominator ends, per basis, so
    // the shaded width always equals the % we report.
    timeline() {
      const hire = toDate(this.hireDate)
      const cohab = toDate(this.cohabitationDate)
      const sep = toDate(this.separationDate)
      if (!hire || !cohab || !sep) return null

      // Basis-dependent accrual end (the denominator end), its label, the ratio,
      // and whether the pension keeps accruing past that point (arrow vs cap).
      let end, endLabel, ratio, continues
      if (this.isOntario) {
        // DIVII PATCH: a former member who left before separation stopped accruing then.
        end = toDate(this.flvdServiceEnd) || sep
        endLabel = this.flvdServiceEnd === this.separationDate ? 'Date of Separation' : 'End of Employment'
        ratio = this.ontarioRatio; continues = false
      } else if (this.isDeferred) {
        // DIVII PATCH: former member: the pension bar ends on the employment end date.
        end = toDate(this.terminationDate) || this.valuationDateObj
        endLabel = 'End of Employment'; ratio = this.proportionDuringRelationship; continues = false
      } else if (this.isInPay) {
        end = toDate(this.retirementDate) || this.valuationDateObj
        endLabel = 'Date of Retirement'; ratio = this.proportionDuringRelationship; continues = true
      } else if (this.pensionSource === 'statement' && this.valuationBasis === 'continue') {
        const age = (this.statementScenarios[0] && this.statementScenarios[0].age) || 65
        end = this.dateAtAge(age) || this.valuationDateObj
        endLabel = `Retirement (age ${age})`
        const proj = this.yearsOfService + Math.max(0, age - this.currentAge)
        ratio = proj > 0 ? Math.min(this.yearsMarried / proj, 1) : 0
        continues = true
      } else {
        // Active terminate (default) and the salary-formula path: value to date.
        end = this.valuationDateObj; endLabel = 'Valuation Date'
        ratio = this.proportionDuringRelationship; continues = false
      }
      if (!end) return null

      const ovStart = new Date(Math.max(hire.getTime(), cohab.getTime()))
      const ovEnd = new Date(Math.min(sep.getTime(), end.getTime()))
      const hasOverlap = ovEnd.getTime() > ovStart.getTime()

      const dates = [hire, cohab, sep, end]
      const minT = Math.min(...dates.map(d => d.getTime()))
      const maxT = Math.max(...dates.map(d => d.getTime()))
      const sp = (maxT - minT) || 1
      const W = 1000, PAD = 80, usable = W - 2 * PAD
      const x = d => PAD + ((d.getTime() - minT) / sp) * usable
      const yrs = (a, b) => calculateYearsBetween(a, b)

      // Distinct date markers (Ontario's end coincides with separation), sorted.
      const raw = [
        { x: x(hire), label: 'Date of Employment', t: hire.getTime() },
        { x: x(cohab), label: 'Date of Cohabitation', t: cohab.getTime() },
        { x: x(sep), label: 'Date of Separation', t: sep.getTime() },
        { x: x(end), label: endLabel, t: end.getTime() },
      ].sort((a, b) => a.x - b.x)
      const markers = []
      raw.forEach(m => {
        const dup = markers.find(p => Math.abs(p.t - m.t) < 86400000)
        if (dup) return
        // Stagger labels that would collide (close x → drop to a lower line).
        const prev = markers[markers.length - 1]
        m.level = prev && (m.x - prev.x) < 120 ? (prev.level === 0 ? 1 : 0) : 0
        markers.push(m)
      })

      return {
        pension: { x1: x(hire), x2: x(end), years: yrs(hire, end), continues },
        relationship: { x1: x(cohab), x2: x(sep), years: yrs(cohab, sep) },
        overlap: hasOverlap ? { x1: x(ovStart), x2: x(ovEnd), years: yrs(ovStart, ovEnd) } : null,
        markers,
        ratio,
      }
    },
    // The scenario the lawyer has selected to record back in Divii, normalised
    // to the figures Divii wants (marital value + post-tax marital portion).
    selectedResult() {
      // Ontario: a single prescribed FLV — recorded back via the opt-in toggle.
      // The imputed value (family-law portion) maps onto the same payload shape
      // as the BC port-back so Divii's handler is identical for both regimes.
      if (this.isOntario) {
        // DIVII PATCH: recording is an explicit action (Record button) for both
        // regimes now, so the payload is no longer gated by the old opt-in checkbox.
        if (!this.ontarioResults) return null
        const o = this.ontarioResults
        return {
          scenario: `Ontario Family Law Value (${this.ontarioFormCode})`,
          maritalValue: o.imputedValue,            // imputed value (pre-tax) = family-law portion
          maritalPostTax: o.imputedValuePostTax,   // imputed value, after-tax estimate
          spousePreTax: o.spouseTransfer,          // spouse's share, pre-tax
          spousePostTax: o.spouseTransferPostTax,  // spouse's share, after-tax estimate
          totalValue: o.flv,                       // full prescribed FLV
          maritalPercentage: o.ratio,
        }
      }
      if (this.selectedScenarioIdx == null) return null
      const r = this.results[this.selectedScenarioIdx]
      if (!r) return null
      return {
        scenario: r.fullLabel || r.shortLabel,
        maritalValue: r.marital,            // Marital Portion Value (pre-tax)
        maritalPostTax: r.maritalPosttax,   // Post-Tax Marital Portion
        spousePreTax: r.spousePretax,       // spouse's 50% pre-tax share
        spousePostTax: r.spousePosttax,     // spouse's 50% post-tax share
        totalValue: r.total,
        maritalPercentage: r.maritalPercentage,
      }
    },
    // DIVII PATCH: the two recordable figures for the chosen result.
    recordRows() {
      const r = this.selectedResult
      if (!r) return []
      return this.isOntario
        ? [
            { basis: 'pre', label: 'Imputed Value, pre-tax (family-law portion)', amount: r.maritalValue },
            { basis: 'post', label: 'Imputed Value, post-tax estimate', amount: r.maritalPostTax },
          ]
        : [
            { basis: 'pre', label: 'Marital Portion Value (pre-tax)', amount: r.maritalValue },
            { basis: 'post', label: 'Post-Tax Marital Portion', amount: r.maritalPostTax },
          ]
    },
    recordAmount() {
      const row = this.recordRows.find(x => x.basis === this.recordTaxBasis)
      return row ? row.amount : null
    },
    pensionHasValue() {
      const v = this.pensionProp ? this.pensionProp.value : null
      return v !== undefined && v !== null && String(v).trim() !== '' && Number(v) !== 0
    },
    // Warn (but allow) when the valued client is not the pension's owner in the builder.
    recordOwnerMismatch() {
      const owner = this.pensionProp && this.pensionProp.ownerName
      if (!owner || !this.clientName) return false
      return owner.trim().toLowerCase() !== this.clientName.trim().toLowerCase()
    },
    recordDisabledReason() {
      if (!this.canRecordProp) return 'Your role can view this valuation but cannot change the agreement.'
      if (!this.pensionProp) return 'Choose the pension being valued under Personal Pension Information to enable recording.'
      if (!this.selectedResult) return ''
      if (!this.recordTaxBasis) return 'Select the pre-tax or post-tax figure to record.'
      return ''
    },
    // Full methodology + assumptions appendix (Report Details section + PDF).
    reportDetailRows() {
      const pct = n => `${Number(n).toFixed(1)}%`
      const rows = []
      if (this.isOntario) {
        rows.push(['Valuation method', `Prescribed Family Law Value (FSRA Form ${this.ontarioFormCode}), O. Reg. 287/11`])
        rows.push(['Basis', 'CIA commuted value (§3500); termination basis as at the separation date'])
        if (!this.isInPay) {
          rows.push(['Retirement age', this.ontarioSubsidized
            ? '50/50 rule — average of the highest-value and earliest-unreduced ages'
            : 'Highest-value commencement age (50/50 rule not applied)'])
        }
        rows.push(['Valuation date (FLVD)', this.fmtDate(this.separationDate)])
        rows.push(['Marital proportion', 'Plan membership during the relationship ÷ membership to the FLVD'])
        if (this.ontarioDeferredProration < 1) {
          // DIVII PATCH
          rows.push(['Figures prorated', `Employment ended after the FLVD; entered deferred pension × ${this.fmtPct(this.ontarioDeferredProration)} (service to FLVD ÷ total service)`])
        }
        rows.push(['Maximum spouse share', '50% of the imputed value (by transfer or offset)'])
      } else {
        rows.push(['Valuation method', 'Survival-weighted present value (CIA Standards of Practice style)'])
        rows.push(['Valuation date', this.fmtDate(this.valuationDate)])
        if (this.isDeferred) {
          // DIVII PATCH: former member basis line.
          rows.push(['Valuation basis', `Former member: service frozen at ${this.fmtDate(this.terminationDate)}; deferred pension indexed at CPI to commencement`])
        } else if (!this.isInPay) {
          rows.push(['Valuation basis', this.valuationBasis === 'terminate'
            ? 'Termination — service to date; deferred pension indexed at CPI to commencement'
            : 'Continue to retirement — projected service & salary'])
        } else {
          rows.push(['Valuation basis', 'In pay — immediate annuity from the current age'])
        }
      }
      rows.push(['Mortality', this.mortalityLabel])
      rows.push(['Discount rate', `${pct(this.discountRate)} for years 1–${this.selectYears}, then ${pct(this.discountRate2)} (select-and-ultimate, from the valuation date)`])
      rows.push(['Indexation', this.pensionIndexed
        ? (this.inflationRate === this.inflationRate2
            ? pct(this.inflationRate)
            : `${pct(this.inflationRate)} for years 1–${this.selectYears}, then ${pct(this.inflationRate2)}`)
        : 'Not indexed'])
      rows.push(['Guarantee period', `${this.guaranteeYears} years`])
      if (!this.isInPay && !this.isOntario && this.pensionSource === 'formula') {
        rows.push(['Max pensionable service', `${this.maxServiceYears} years`])
      }
      rows.push(['Payments', 'Monthly, payable in advance'])
      rows.push(["Spouse's share", `${this.spouseShare}% of the marital portion`])
      rows.push(['Simplifications', 'Single life only (no joint-and-survivor or pre-retirement death benefit)'
        + (this.isFederal ? '; federal plans — screening estimate only' : '')])
      return rows
    },
    // Shared actuarial assumptions for the valuation engine.
    actuarialParams() {
      return {
        valuationYear: this.valuationDateObj.getFullYear(),
        nominalRate1: this.discountRate / 100,
        nominalRate2: this.discountRate2 / 100,
        selectYears: this.selectYears,
        indexationRate: this.pensionIndexed ? this.inflationRate / 100 : 0,
        indexationRate2: this.pensionIndexed ? this.inflationRate2 / 100 : 0,
        guaranteeYears: this.guaranteeYears,
        gender: this.gender || 'Male',
        mortalitySector: this.mortalitySector,
      }
    },
    // ── Ontario FLV (valued AS AT the separation date / FLVD) ─────
    separationYear() {
      const d = toDate(this.separationDate)
      return d ? d.getFullYear() : this.today.getFullYear()
    },
    ageAtFLVD() {
      return this.birthdate && this.separationDate
        ? calculateAge(this.birthdate, toDate(this.separationDate))
        : 0
    },
    // Pensionable service to the FLVD, and the part during the relationship.
    // DIVII PATCH: former member: plan membership ended on the employment end
    // date. When that falls before the separation date it caps FLVD service
    // (leaving after separation changes nothing: termination basis already
    // assumes termination at the FLVD).
    flvdServiceEnd() {
      if (this.isDeferred && this.terminationDate && this.separationDate) {
        const sep = toDate(this.separationDate)
        const term = toDate(this.terminationDate)
        if (sep && term && term < sep) return this.terminationDate
      }
      return this.separationDate
    },
    // DIVII PATCH: former member who left AFTER the separation date. The deferred
    // pension entered is the entitlement at the leaving date, which includes
    // service after the FLVD, while the FL-4 is on a termination-at-FLVD basis.
    // Approximate that basis by prorating the figures by service to the FLVD over
    // total service, unless the lawyer confirms the figures are FLVD-dated.
    ontarioLeftAfterSeparation() {
      if (!this.isDeferred || !this.terminationDate || !this.separationDate) return false
      const sep = toDate(this.separationDate)
      const term = toDate(this.terminationDate)
      return !!(sep && term && term > sep)
    },
    ontarioTotalService() {
      return this.hireDate && this.terminationDate
        ? calculateYearsBetween(this.hireDate, this.terminationDate)
        : 0
    },
    ontarioDeferredProration() {
      if (!this.ontarioLeftAfterSeparation || this.ontarioFiguresAtFLVD) return 1
      if (!(this.ontarioTotalService > 0) || !(this.serviceToFLVD > 0)) return 1
      return Math.min(this.serviceToFLVD / this.ontarioTotalService, 1)
    },
    serviceToFLVD() {
      return this.hireDate && this.flvdServiceEnd
        ? calculateYearsBetween(this.hireDate, this.flvdServiceEnd)
        : 0
    },
    relServiceToFLVD() {
      // effectiveCohabitationDate already clamps cohabitation to ≥ hire date.
      return calculateYearsBetween(this.effectiveCohabitationDate, this.flvdServiceEnd)
    },
    ontarioRatio() {
      return this.serviceToFLVD > 0
        ? Math.min(this.relServiceToFLVD / this.serviceToFLVD, 1)
        : 0
    },
    // Engine params re-anchored to the FLVD (commuted-value / §3500 basis).
    ontarioParams() {
      return {
        ageAtFLVD: this.ageAtFLVD,
        gender: this.gender || 'Male',
        separationYear: this.separationYear,
        nominalRate1: this.discountRate / 100,
        nominalRate2: this.discountRate2 / 100,
        selectYears: this.selectYears,
        indexationRate: this.pensionIndexed ? this.inflationRate / 100 : 0,
        indexationRate2: this.pensionIndexed ? this.inflationRate2 / 100 : 0,
        guaranteeYears: this.guaranteeYears,
        mortalitySector: this.mortalitySector,
      }
    },
    // Full Ontario FLV result: preliminary value, 50/50 components, imputed
    // value (the family-law portion), and the 50% statutory cap. Pre-tax.
    ontarioResults() {
      if (!this.isOntario) return null
      const scenarios = this.isInPay
        ? [{
            age: this.ageAtFLVD,
            lifetimeAnnual: this.annualPensionInPay,
            bridgeAnnual: this.bridgeInPayMonthly * 12,
          }]
        : this.ontarioScenarios.map(sc => ({
            age: sc.age,
            // DIVII PATCH: × ontarioDeferredProration (1 unless a former member
            // left after the FLVD and the figures are not FLVD-dated).
            lifetimeAnnual: (sc.lifetimeMonthly || 0) * 12 * this.ontarioDeferredProration,
            bridgeAnnual: (sc.bridgeMonthly || 0) * 12 * this.ontarioDeferredProration,
          }))
      const flvRes = valueOntarioFLV({
        ...this.ontarioParams,
        scenarios,
        earliestUnreducedAge: this.ontarioEarliestUnreducedAge,
        subsidizedEarly: this.ontarioSubsidized,
        inPay: this.isInPay,
      })
      const imputedValue = flvRes.flv * this.ontarioRatio
      const statutoryCap = 0.5 * imputedValue
      const spousePct = Math.min(this.spouseShare, 50) / 100
      const spouseTransfer = imputedValue * spousePct
      const taxRate = this.ontarioTaxRate / 100
      return {
        ...flvRes,
        ratio: this.ontarioRatio,
        imputedValue,
        statutoryCap,
        spouseTransfer,
        spousePct,
        taxRate,
        imputedValuePostTax: imputedValue * (1 - taxRate),
        spouseTransferPostTax: spouseTransfer * (1 - taxRate),
      }
    },
    allScenarios() {
      // DIVII PATCH: former member: no further accrual, so every scenario is
      // "start the frozen deferred pension at age X".
      if (this.isDeferred) {
        const fixed = this.visibleFixedScenarios.map((s, i) => ({
          n: i + 1,
          label: s.label,
          stopWork: true,
          retirementAge: s.retirementAge,
          taxRate: s.taxRate,
        }))
        const custom = this.customScenarios.map((c, i) => ({
          n: fixed.length + i + 1,
          label: `Start pension at ${c.age}`,
          stopWork: true,
          retirementAge: c.age,
          taxRate: c.taxRate,
        }))
        return [...fixed, ...custom]
      }
      const fixed = this.fixedScenarios.map((s, i) => ({
        n: i + 1,
        label: s.label,
        stopWork: s.stopWork,
        retirementAge: s.retirementAge,
        taxRate: s.taxRate,
      }))
      const custom = this.customScenarios.map((c, i) => ({
        n: fixed.length + i + 1,
        label: `Continue work, retire at ${c.age}`,
        stopWork: false,
        retirementAge: c.age,
        taxRate: c.taxRate,
      }))
      return [...fixed, ...custom]
    },
    results() {
      if (!this.calculated) return []
      // Ontario uses its own FLV report (ontarioResults), not the setoff tables.
      if (this.isOntario) return []

      const buildShares = (total, marital, taxPct) => {
        const spousePretax = marital * (this.spouseShare / 100)
        const taxRate = taxPct / 100
        const spousePosttax = spousePretax * (1 - taxRate)
        return { spousePretax, taxRate, spousePosttax, maritalPosttax: marital * (1 - taxRate) }
      }

      // ── Already retired / in pay: lifetime + bridge, immediate ──
      if (this.isInPay) {
        const v = valuePensionStream({
          lifetimeAnnual: this.annualPensionInPay,
          bridgeAnnual: this.bridgeInPayMonthly * 12,
          currentAge: this.currentAge,
          retirementAge: this.currentAge, // already in pay → deferral 0
          todaysDollars: false,
          ...this.actuarialParams,
          guaranteeYears: this.guaranteeYears,
        })
        const ratio = this.proportionDuringRelationship
        const marital = v.total * ratio
        return [{
          shortLabel: 'Pension in pay',
          fullLabel: 'Pension in pay (retired ' + this.fmtDate(this.retirementDate) + ')',
          total: v.total, marital, maritalPercentage: ratio,
          bridgeValue: v.bridgeValue,
          ...buildShares(v.total, marital, this.inPayTaxRate),
          pensionStartDate: toDate(this.retirementDate) || this.today,
          annualPension: this.annualPensionInPay,
        }]
      }

      // ── Active member, statement estimate: lifetime + bridge per age ──
      if (this.pensionSource === 'statement') {
        const terminate = this.isTerminateBasis // DIVII PATCH: forced for a former member
        return this.statementScenarios.map((sc, i) => {
          // Terminate basis: value service to date (frozen denominator; the
          // deferred pension grows at CPI). Continue basis: project service to
          // retirement (denominator) and wage-escalate the figure.
          const denomService = terminate
            ? this.yearsOfService
            : this.yearsOfService + Math.max(0, sc.age - this.currentAge)
          const ratio = denomService > 0 ? Math.min(this.yearsMarried / denomService, 1) : 0
          const v = valuePensionStream({
            lifetimeAnnual: (sc.lifetimeMonthly || 0) * 12,
            bridgeAnnual: (sc.bridgeMonthly || 0) * 12,
            currentAge: this.currentAge,
            retirementAge: sc.age,
            todaysDollars: true,
            // Deferred (terminate) pension escalates at CPI; a "continue
            // working" figure escalates with wages until retirement. Split at
            // the select boundary (post-N uses the ultimate indexation rate).
            wageGrowth: terminate
              ? (this.pensionIndexed ? this.inflationRate / 100 : 0)
              : this.expectedSalaryGrowth / 100,
            wageGrowth2: terminate
              ? (this.pensionIndexed ? this.inflationRate2 / 100 : 0)
              : this.expectedSalaryGrowth / 100,
            ...this.actuarialParams,
            guaranteeYears: this.guaranteeYears,
          })
          const marital = v.total * ratio
          const startDate = new Date(this.valuationDateObj.getTime())
          startDate.setDate(startDate.getDate() + Math.round(v.deferYears * 365))
          return {
            shortLabel: `Retire at ${sc.age}`,
            fullLabel: `Scenario ${i + 1}: retire at ${sc.age}`,
            total: v.total, marital, maritalPercentage: ratio,
            bridgeValue: v.bridgeValue,
            ...buildShares(v.total, marital, sc.taxRate),
            pensionStartDate: startDate,
            annualPension: (sc.lifetimeMonthly || 0) * 12,
          }
        })
      }

      // DIVII PATCH: former member, salary formula. The accrued pension is fixed
      // (earnings at leaving × accrual × frozen service, capped at the plan's
      // max service) and, like a statement "terminate" figure, is indexed at
      // CPI until it starts. Reuses the statement engine so both former-member
      // paths are valued identically.
      if (this.isDeferred) {
        const cap = this.maxServiceYears > 0 ? this.maxServiceYears : Infinity
        const service = Math.min(this.yearsOfService, cap)
        const accruedAnnual = this.currentSalary * (this.benefitAccrualRate / 100) * service
        const ratio = this.proportionDuringRelationship
        return this.allScenarios.map(sc => {
          const v = valuePensionStream({
            lifetimeAnnual: accruedAnnual,
            bridgeAnnual: 0,
            currentAge: this.currentAge,
            retirementAge: sc.retirementAge,
            todaysDollars: true,
            wageGrowth: this.pensionIndexed ? this.inflationRate / 100 : 0,
            wageGrowth2: this.pensionIndexed ? this.inflationRate2 / 100 : 0,
            ...this.actuarialParams,
            guaranteeYears: this.guaranteeYears,
          })
          const marital = v.total * ratio
          const startDate = new Date(this.valuationDateObj.getTime())
          startDate.setDate(startDate.getDate() + Math.round(v.deferYears * 365))
          return {
            shortLabel: sc.label,
            fullLabel: `Scenario ${sc.n}: ${sc.label}`,
            total: v.total, marital, maritalPercentage: ratio,
            ...buildShares(v.total, marital, sc.taxRate),
            pensionStartDate: startDate,
            annualPension: accruedAnnual,
          }
        })
      }

      // ── Active member, salary formula: projected scenarios ──
      return this.allScenarios.map(sc => {
        const { values, details } = calculatePensionValue({
          currentAge: this.currentAge,
          yearsOfService: this.yearsOfService,
          currentSalary: this.currentSalary,
          salaryGrowth: this.expectedSalaryGrowth / 100,
          yearsMarried: this.yearsMarried,
          stopWorkNow: sc.stopWork,
          retirementAge: sc.retirementAge,
          benefitAccrualRate: this.benefitAccrualRate / 100,
          maxService: this.maxServiceYears,
          ...this.actuarialParams,
        })
        const spousePretax = values.marital * (this.spouseShare / 100)
        const taxRate = sc.taxRate / 100
        const spousePosttax = spousePretax * (1 - taxRate)
        const startDate = new Date(this.valuationDateObj.getTime())
        startDate.setDate(startDate.getDate() + Math.round(details.yearsToRetirement * 365))
        return {
          shortLabel: sc.label,
          fullLabel: `Scenario ${sc.n}: ${sc.label}`,
          total: values.total,
          marital: values.marital,
          maritalPercentage: values.maritalPercentage,
          spousePretax,
          taxRate,
          spousePosttax,
          // Full marital portion after tax (= 2× the 50% spouse post-tax share).
          maritalPosttax: values.marital * (1 - taxRate),
          pensionStartDate: startDate,
          annualPension: details.annualPension,
          lifeExpectancy: details.lifeExpectancy,
        }
      })
    },
    // Chunk scenarios into rows of at most 3 — each becomes its own results
    // table (with charts beneath). A 4th+ scenario spills to the next table.
    // The last group is padded to 3 columns with empty placeholders so every
    // table shares the same fixed column layout (e.g. Scenario 4 lines up
    // under Scenario 1).
    scenarioGroups() {
      const groups = []
      this.results.forEach((r, i) => {
        if (i % 3 === 0) groups.push([])
        groups[groups.length - 1].push({ r, i })
      })
      groups.forEach(g => {
        while (g.length < 3) g.push({ r: null, i: null })
      })
      return groups
    },
  },

  watch: {
    // Live-update results & charts once the user has calculated once.
    results() {
      if (this.calculated) this.$nextTick(this.renderCharts)
    },
    // Persist state to the parent app (Divii) on any change.
    selectedClientIdx() {
      // Relationship dates come straight from the matter (read-only props), so
      // there is nothing to copy here. Seed the plan jurisdiction from the
      // matter's province of residence when not already set (still overridable —
      // the plan may differ).
      const c = this.selectedClient
      if (c && !this.planJurisdiction) {
        this.planJurisdiction = c.province || this.provinceProp || ''
      }
      this.emitState()
    },
    // Re-seed the plan jurisdiction when the matter's residence prop changes,
    // unless the lawyer has already overridden it (then leave their choice).
    provinceProp(newVal, oldVal) {
      if (!this.planJurisdiction || this.planJurisdiction === oldVal) {
        this.planJurisdiction = newVal || ''
      }
    },
    hireDate() { this.emitState() },
    currentSalary() { this.emitState() },
    expectedSalaryGrowth() { this.emitState() },
    pensionStatus() { this.emitState() },
    pensionSource() { this.emitState() },
    valuationBasis() { this.emitState() },
    retirementDate() { this.emitState() },
    terminationDate() { this.emitState() }, // DIVII PATCH
    annualPensionInPay() { this.emitState() },
    bridgeInPayMonthly() { this.emitState() },
    inPayTaxRate() { this.emitState() },
    statementScenarios: { deep: true, handler() { this.emitState() } },
    valuationDate() { this.emitState() },
    planJurisdiction() {
      // Federal pensions are fully CPI-indexed and government-sector — set those
      // as the defaults when Federal is selected (still user-overridable after).
      if (this.planJurisdiction === 'FED') {
        this.pensionIndexed = true
        this.mortalitySector = 'public'
      }
      this.emitState()
    },
    ontarioScenarios: { deep: true, handler() { this.emitState() } },
    ontarioEarliestUnreducedAge() { this.emitState() },
    ontarioSubsidized() { this.emitState() },
    ontarioFiguresAtFLVD() { this.emitState() }, // DIVII PATCH
    ontarioTaxRate() { this.emitState() },
    ontarioRecord() { this.emitState() },
    recordTaxBasis() { this.emitState() }, // DIVII PATCH
    discountRate() { this.emitState() },
    discountRate2() { this.emitState() },
    selectYears() { this.emitState() },
    inflationRate() { this.emitState() },
    inflationRate2() { this.emitState() },
    pensionIndexed() { this.emitState() },
    guaranteeYears() { this.emitState() },
    mortalitySector() { this.emitState() },
    benefitAccrualRate() { this.emitState() },
    maxServiceYears() { this.emitState() },
    includeDetailsInPdf() { this.emitState() },
    selectedScenarioIdx() { this.emitState() },
    // Port the chosen scenario's key figures back to the parent app (Divii).
    // Fires on selection and whenever the selected scenario's numbers change.
    selectedResult(v) { this.$emit('select-result', v) },
    spouseShare() { this.emitState() },
    fixedScenarios: { deep: true, handler() { this.emitState() } },
    customScenarios: { deep: true, handler() { this.emitState() } },
  },

  created() {
    // Non-reactive store for Chart.js instances (must not be observed by Vue).
    this.chartInstances = []
  },

  methods: {
    addCustom() {
      this.customScenarios.push({ age: 65, taxRate: 20 })
    },
    removeCustom(i) {
      this.customScenarios.splice(i, 1)
    },
    addStatement() {
      this.statementScenarios.push({ age: 65, lifetimeMonthly: 0, bridgeMonthly: 0, taxRate: 22 })
    },
    removeStatement(i) {
      if (this.statementScenarios.length > 1) this.statementScenarios.splice(i, 1)
    },
    // Birthdate + `age` years → a Date (for the timeline's retirement marker).
    dateAtAge(age) {
      const b = toDate(this.birthdate)
      if (!b || !(age > 0)) return null
      const whole = Math.floor(age)
      const d = new Date(b.getTime())
      d.setFullYear(d.getFullYear() + whole)
      d.setDate(d.getDate() + Math.round((age - whole) * 365.25))
      return d
    },
    addOntario() {
      this.ontarioScenarios.push({ age: 65, lifetimeMonthly: 0, bridgeMonthly: 0 })
    },
    removeOntario(i) {
      if (this.ontarioScenarios.length > 1) this.ontarioScenarios.splice(i, 1)
    },
    // DIVII PATCH: the host owns which pension is being valued (it swaps the saved
    // state and remounts this component), so just report the choice.
    onSelectPension(event) {
      const raw = event && event.target ? event.target.value : event
      this.$emit('select-pension', raw === '' || raw === null || raw === undefined ? null : Number(raw))
    },
    // DIVII PATCH: hand the chosen figure to the host, which writes it to the
    // pension in the builder (asking first if a value is already there).
    recordInAgreement() {
      if (this.recordDisabledReason || this.recordAmount == null) return
      this.$emit('record-result', {
        pensionId: this.pensionProp.id,
        basis: this.recordTaxBasis,
        orderOfTax: this.recordTaxBasis === 'pre' ? 'before-tax' : 'after-tax',
        amount: Math.round(this.recordAmount * 100) / 100,
        scenario: this.selectedResult.scenario,
        result: this.selectedResult,
      })
    },

    generate() {
      const missing = []
      if (!this.selectedClient) missing.push('Client')
      if (!this.hireDate) missing.push('Date of Hire')
      // DIVII PATCH: former member needs an employment end date after the hire
      // date and not after the valuation date (a future date means still working).
      const dateErrors = []
      if (this.isDeferred) {
        if (!this.terminationDate) {
          missing.push('Employment End Date')
        } else {
          const hire = toDate(this.hireDate)
          const term = toDate(this.terminationDate)
          if (hire && term && term <= hire) {
            dateErrors.push('The Employment End Date must be after the Date of Hire.')
          }
          if (term && term > this.valuationDateObj) {
            dateErrors.push('The Employment End Date is after the valuation date. If the member is still employed, choose "Still working / not yet retired".')
          }
        }
      }
      if (this.isInPay) {
        if (!this.retirementDate) missing.push('Retirement Date')
        if (!this.annualPensionInPay || this.annualPensionInPay <= 0) missing.push('Annual Lifetime Pension in Pay')
      } else if (this.isOntario) {
        if (!this.ontarioScenarios.some(sc => sc.lifetimeMonthly > 0)) {
          missing.push('an accrued deferred pension estimate (lifetime $/mo)')
        }
      } else if (this.pensionSource === 'statement') {
        if (!this.statementScenarios.some(sc => sc.lifetimeMonthly > 0)) {
          missing.push('a statement pension estimate (lifetime $/mo)')
        }
      } else {
        if (!this.currentSalary || this.currentSalary <= 0) missing.push('Current Annual Salary')
      }
      // Relationship dates are read-only here — they come from the Divii matter.
      // If they're blank, the fix is in the builder, not the calculator, so give
      // that instruction rather than a generic "please enter" error.
      const missingDates = []
      if (!this.cohabitationDate) missingDates.push('marriage / cohabitation')
      if (!this.separationDate) missingDates.push('separation')
      if (missing.length || missingDates.length || dateErrors.length) {
        const parts = [...dateErrors] // DIVII PATCH
        if (missing.length) {
          parts.push('Please enter all required information: ' + missing.join(', ') + '.')
        }
        if (missingDates.length) {
          const label = missingDates.join(' and ') + (missingDates.length > 1 ? ' dates' : ' date')
          parts.push(`The ${label} must be filled in the Relationship section of the builder.`)
        }
        this.errorMsg = parts.join(' ')
        this.calculated = false
        // Surface the banner — it sits below the button, so scroll it into view
        // instead of letting it appear silently off-screen.
        this.$nextTick(() => {
          if (this.$refs.errorBox) {
            this.$refs.errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
        return
      }
      this.errorMsg = ''
      this.calculated = true
      this.view = 'report'
      // Wait for the report (and its canvases) to mount before drawing charts.
      this.$nextTick(this.renderCharts)
    },

    renderCharts() {
      this.destroyCharts()
      const images = []
      this.results.forEach((r, i) => {
        const refArr = this.$refs['chart' + i]
        const canvas = Array.isArray(refArr) ? refArr[0] : refArr
        if (!canvas) return
        const marital = r.marital
        const nonMarital = Math.max(0, r.total - r.marital)
        const chart = new Chart(canvas, {
          type: 'doughnut',
          data: {
            labels: ['Marital', 'Non-Marital'],
            datasets: [{
              data: [marital, nonMarital],
              backgroundColor: ['#e8b340', '#3F6772'],
              borderColor: '#ffffff',
              borderWidth: 2,
            }],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            // Disabled so the chart paints synchronously and toBase64Image()
            // below captures the finished chart for the print worksheet.
            animation: false,
            cutout: '55%',
            plugins: {
              legend: {
                position: 'bottom',
                labels: { font: { size: 9, family: 'Inter' }, boxWidth: 8, padding: 5 },
              },
              tooltip: {
                callbacks: {
                  label: ctx => ` ${ctx.label}: ${this.fmtMoney(ctx.parsed)}`,
                },
              },
            },
          },
        })
        this.chartInstances.push(chart)
        // Snapshot for the PDF (rendered against a white background).
        images[i] = chart.toBase64Image('image/png', 1)
      })
      this.chartImages = images
    },
    destroyCharts() {
      if (!this.chartInstances) { this.chartInstances = []; return }
      this.chartInstances.forEach(c => c.destroy())
      this.chartInstances = []
    },

    printReport() {
      // Gate the print CSS on a body class so the hide-everything rules
      // only apply while THIS component is printing — an ungated
      // `body * { visibility: hidden }` blanks every other print on the
      // page (e.g. the Support Calculator worksheet).
      document.body.classList.add('pension-printing')
      const cleanup = () => {
        document.body.classList.remove('pension-printing')
        window.removeEventListener('afterprint', cleanup)
      }
      window.addEventListener('afterprint', cleanup)
      window.print()
    },

    // ── State persistence — emit a serializable snapshot to the parent app.
    emitState() {
      this.$emit('update-state', this.computeFullState())
    },
    computeFullState() {
      return {
        schemaVersion: 1,
        selectedClientIdx: this.selectedClientIdx,
        hireDate: this.hireDate,
        currentSalary: this.currentSalary,
        expectedSalaryGrowth: this.expectedSalaryGrowth,
        pensionStatus: this.pensionStatus,
        pensionSource: this.pensionSource,
        valuationBasis: this.valuationBasis,
        retirementDate: this.retirementDate,
        terminationDate: this.terminationDate, // DIVII PATCH
        annualPensionInPay: this.annualPensionInPay,
        bridgeInPayMonthly: this.bridgeInPayMonthly,
        inPayTaxRate: this.inPayTaxRate,
        statementScenarios: JSON.parse(JSON.stringify(this.statementScenarios)),
        cohabitationDate: this.cohabitationDate,
        separationDate: this.separationDate,
        valuationDate: this.valuationDate,
        planJurisdiction: this.planJurisdiction,
        ontarioScenarios: JSON.parse(JSON.stringify(this.ontarioScenarios)),
        ontarioEarliestUnreducedAge: this.ontarioEarliestUnreducedAge,
        ontarioSubsidized: this.ontarioSubsidized,
        ontarioFiguresAtFLVD: this.ontarioFiguresAtFLVD, // DIVII PATCH
        ontarioTaxRate: this.ontarioTaxRate,
        ontarioRecord: this.ontarioRecord,
        recordTaxBasis: this.recordTaxBasis, // DIVII PATCH
        discountRate: this.discountRate,
        discountRate2: this.discountRate2,
        selectYears: this.selectYears,
        inflationRate: this.inflationRate,
        inflationRate2: this.inflationRate2,
        pensionIndexed: this.pensionIndexed,
        guaranteeYears: this.guaranteeYears,
        mortalitySector: this.mortalitySector,
        benefitAccrualRate: this.benefitAccrualRate,
        maxServiceYears: this.maxServiceYears,
        includeDetailsInPdf: this.includeDetailsInPdf,
        selectedScenarioIdx: this.selectedScenarioIdx,
        selectedResult: this.selectedResult,
        spouseShare: this.spouseShare,
        fixedScenarios: JSON.parse(JSON.stringify(this.fixedScenarios)),
        customScenarios: JSON.parse(JSON.stringify(this.customScenarios)),
      }
    },

    // ── Formatting ───────────────────────────────────────────────
    fmtMoney(n) {
      return (n || 0).toLocaleString('en-CA', {
        style: 'currency', currency: 'CAD', minimumFractionDigits: 2, maximumFractionDigits: 2,
      })
    },
    fmtPct(frac) {
      return (frac * 100).toFixed(2) + '%'
    },
    fmt1(n) { return (n || 0).toFixed(1) },
    fmt2(n) { return (n || 0).toFixed(2) },
    fmtDate(d) {
      const date = toDate(d)
      if (!date) return 'N/A'
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      return `${mm}/${dd}/${date.getFullYear()}`
    },
    fmtLongDate(d) {
      const date = toDate(d)
      if (!date) return 'N/A'
      return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
    },
  },

  beforeDestroy() {
    this.destroyCharts()
  },
}
</script>

<style scoped>
/* ───────────────────────────────────────────────────────────────────────────
   DIVII PENSION VALUE ESTIMATOR — visual style
   Aligned with the Divii app shell (matches the Support Calculator):
   dark teal/slate banners, teal-blue subheadings, gold accent for highlights,
   Inter typography.
   ─────────────────────────────────────────────────────────────────────────── */
.pension-calc {
  --banner-bg:      #2d4a55;
  --banner-text:    #ffffff;
  --heading:        #3F6772;
  --heading-strong: #2d4a55;
  --text:           #1e293b;
  --text-muted:     #64748b;
  --text-subtle:    #94a3b8;
  --bg-page:        #ffffff;
  --bg-card:        #f8fafc;
  --bg-row-alt:     #f1f5f9;
  --bg-highlight:   #d6e6ee;
  --accent-gold:    #e8b340;
  --accent-gold-bg: #fdf4dc;
  --accent-teal:    #4a7285;
  --border:         #e2e8f0;
  --border-input:   #cbd5e1;
  --cta-bg:         #3F6772;
  --cta-bg-hover:   #2d4a55;
  --cta-text:       #ffffff;
  --warn-bg:        #fef3c7;
  --warn-text:      #92400e;
  --danger:         #dc2626;
  --focus-ring:     0 0 0 3px rgba(58, 100, 112, 0.18);

  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text);
  text-align: left;
  position: relative;
}

/* Header */
.pc-header { margin-bottom: 1.5rem; }
.pc-header h1 {
  font-size: 1.9rem;
  font-weight: 600;
  color: var(--heading-strong);
  margin: 0 0 0.5rem;
  letter-spacing: -0.01em;
}
.pc-intro { color: var(--text-muted); font-size: 0.95rem; margin: 0 0 1rem; }
.pc-disclaimer {
  background: var(--accent-gold-bg);
  border-left: 5px solid var(--accent-gold);
  border-radius: 6px;
  padding: 0.85rem 1rem;
  font-size: 0.85rem;
  color: #5b4708;
  line-height: 1.45;
}

/* ── Page shell ──────────────────────────────────────────────────────
   Entry page: single full-width column (inputs flow into a 2-col grid).
   Report page: full-width report (inputs are summarised in the Summary card). */
.pc-shell--entry,
.pc-shell--report { display: block; }

/* Entry-page inputs: a single full-width column of cards so every dark card
   header lines up (same width, same left edge). Each card's fields are laid
   out horizontally in a responsive grid (.fields) so the form spreads across
   the page and stays short rather than a tall narrow strip. */
.pc-inputs--entry { display: block; }
fieldset.pc-inputs { border: 0; margin: 0; padding: 0; min-width: 0; }

/* Responsive field grid inside each card. On the wide entry page fields flow
   into multiple columns; in the narrow report panel they collapse to one. */
.fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem 1.25rem;
  align-items: end;
}
.fields > .field { margin-bottom: 0; }
.fields .span-all { grid-column: 1 / -1; }
/* Top-align labels so a slider's title lines up with the input's title, then
   give the slider the input's height so its track centers against the input
   box (rather than the slider dropping to the helper-hint line below). */
.fields--align-input { align-items: start; }
.fields--align-input .slider { height: 44px; }

/* Assumptions card: 2×2 grid of sliders, with the dropdown on its own row
   underneath. The wider 2-column tracks keep each slider label on one line. */
.assumptions-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  align-items: end;
}
.assumptions-grid > .field { margin-bottom: 0; }
.assumptions-select { grid-column: 1 / -1; }   /* dropdown spans below the sliders */
.assumptions-select select { max-width: 340px; }
.assumptions-extra { margin-top: 1rem; }
.row-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1rem; }
@media (max-width: 760px) { .row-3 { grid-template-columns: 1fr; } }
.checkbox-line { display: flex; align-items: center; gap: 0.45rem; font-weight: 400; font-size: 0.9rem; color: var(--text); text-transform: none; }
.checkbox-line input { accent-color: var(--accent-teal); }
.sector-select { max-width: 460px; }
.pc-inputs--panel .assumptions-grid { grid-template-columns: 1fr; }
@media (max-width: 760px) {
  .assumptions-grid { grid-template-columns: 1fr; }
}

/* Relationship card: date inputs on the left, calculated outputs on the right */
.rel-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem 1.5rem;
  align-items: start;
}
.rel-inputs { display: flex; flex-direction: column; gap: 1rem; }
.rel-outputs { display: flex; flex-direction: column; gap: 0.6rem; }
.rel-outputs .readout-grid { gap: 0.6rem; margin-top: 0; }
/* Push the outputs down by one field-label height so the top of the first
   output box lines up with the top of the first date input (not its label).
   The calc mirrors `.field label` (line-height + margin-bottom). */
.pc-inputs--entry .rel-outputs { margin-top: calc(0.8rem * 1.5 + 0.35rem); }
/* Relationship Details: dates and derived readouts are all read-only now, so
   stack the two groups vertically (outputs under the dates) instead of the
   two-column input/output split used by the editable Employment section. */
.rel-layout--stacked { grid-template-columns: 1fr; }
.pc-inputs--entry .rel-layout--stacked .rel-outputs { margin-top: 0; }
/* Collapse to a single column in the narrow report panel and on small screens */
.pc-inputs--panel .rel-layout { grid-template-columns: 1fr; }
@media (max-width: 760px) {
  .rel-layout { grid-template-columns: 1fr; }
  .pc-inputs--entry .rel-outputs { margin-top: 0; }
}

/* Report-page panel: fields always stack (the panel is too narrow to split) */
.pc-inputs--panel .fields { display: block; }
.pc-inputs--panel .fields > .field { margin-bottom: 1rem; }
.pc-inputs--panel .fields > .field:last-child { margin-bottom: 0; }

/* Custom retirement ages flow horizontally on the entry page too */
.pc-inputs--entry .custom-age-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem 1.25rem;
}
.pc-inputs--entry .custom-age-row { margin-bottom: 0; }

/* Statement pension-estimate rows */
.stmt-head, .stmt-row {
  display: grid;
  grid-template-columns: 1fr 1.3fr 1.3fr 0.7fr 38px;
  gap: 0.5rem;
  align-items: center;
}
.stmt-head {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--heading);
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 0.4rem;
}
.stmt-row { margin-bottom: 0.5rem; }
.stmt-row input[type="number"] {
  width: 100%;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--border-input);
  border-radius: 8px;
  font: inherit;
  font-size: 0.9rem;
  box-sizing: border-box;
}
.stmt-row .input-prefix { border-radius: 8px; }
.stmt-row .input-prefix input { padding: 0.5rem 0.6rem; font-size: 0.9rem; }
/* Ontario table: no Tax % column → 4-column grid. */
.stmt-head--ont, .stmt-row--ont { grid-template-columns: 1fr 1.3fr 1.3fr 38px; }
.ont-eu-input {
  width: 120px;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--border-input);
  border-radius: 8px;
  font: inherit;
  font-size: 0.9rem;
}

/* Ontario mode banner (teal, distinct from the gold disclaimer). */
.pc-onbanner {
  margin-top: 0.75rem;
  background: #eef5f7;
  border-left: 5px solid var(--accent-teal);
  border-radius: 6px;
  padding: 0.85rem 1rem;
  font-size: 0.85rem;
  color: #234049;
  line-height: 1.45;
}
/* Cautionary banner (e.g. Manitoba — wrong-regime warning). */
.pc-warnbanner {
  margin-top: 0.75rem;
  background: #fbeee0;
  border-left: 5px solid #c2772e;
  border-radius: 6px;
  padding: 0.85rem 1rem;
  font-size: 0.85rem;
  color: #6b3d10;
  line-height: 1.45;
}

/* Ontario FLV result table. */
.flv-table { border-collapse: collapse; width: 100%; font-size: 0.9rem; }
.flv-table td { border: 1px solid var(--border); padding: 0.5rem 0.7rem; }
.flv-table td:first-child { color: var(--text-muted); }
.flv-table td:last-child { text-align: right; font-weight: 600; font-variant-numeric: tabular-nums; }
.flv-table .flv-subtotal td { background: #f1f5f9; }
.flv-table .row-highlight td { background: var(--accent-gold-bg); color: var(--heading-strong); }
.flv-table .flv-note-row td { font-size: 0.78rem; font-style: italic; color: var(--text-muted); text-align: left; font-weight: 400; }
.flv-caveat {
  margin-top: 0.75rem;
  font-size: 0.8rem;
  line-height: 1.45;
  color: #5b4708;
  background: var(--accent-gold-bg);
  border-left: 5px solid var(--accent-gold);
  border-radius: 6px;
  padding: 0.7rem 0.9rem;
}

.entry-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  align-items: flex-start;
  margin-top: 0.25rem;
}
.btn-generate { font-size: 1.05rem; padding: 0.85rem 2.25rem; }
@media (max-width: 760px) { .btn-generate { width: 100%; } }

/* Report-page toolbar (back-to-form link) — full-width bar above both columns */
.report-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.85rem;
  margin-bottom: 1.25rem;
}
.report-toolbar-hint { font-size: 0.8rem; color: var(--text-muted); }
.btn-ghost-sm {
  align-self: flex-start;
  background: transparent;
  border: 1px solid var(--border-input);
  color: var(--accent-teal);
  border-radius: 8px;
  padding: 0.4rem 0.8rem;
  font-family: inherit;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
}
.btn-ghost-sm:hover { background: var(--bg-card); border-color: var(--accent-teal); color: var(--heading-strong); }

/* Section banner — Divii dark teal header pattern */
h2 {
  background: var(--banner-bg);
  color: var(--banner-text);
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  margin: -1.25rem -1.25rem 1.25rem -1.25rem;
  padding: 0.8rem 1.25rem;
  border-radius: 8px 8px 0 0;
}
h3 {
  font-size: 0.95rem;
  font-weight: 600;
  margin: 1.25rem 0 0.5rem;
  color: var(--heading);
}
.sub { color: var(--text-muted); font-size: 0.85rem; margin: -0.5rem 0 1rem; }

/* Panels */
.step {
  background: var(--bg-page);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* Collapsible card header */
.step-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  /* keep the base h2's negative margins (full-width dark bar) — don't reset */
}
.step-toggle:focus-visible { outline: 2px solid var(--accent-teal); outline-offset: 3px; border-radius: 4px; }
.step-chevron {
  font-size: 0.9rem;
  color: var(--text-muted);
  transition: transform 0.15s ease;
  margin-left: 0.5rem;
}
.step-chevron.open { transform: rotate(90deg); }
.assump-summary {
  margin: 0.5rem 0 0;
  font-size: 0.82rem;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  cursor: pointer;
}
.assump-adjust { color: var(--accent-teal); font-weight: 600; }

/* Report Details appendix */
.pdf-optin {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-size: 0.85rem; color: var(--heading); margin-bottom: 0.85rem; cursor: pointer;
}
.details-table { border-collapse: collapse; width: 100%; font-size: 0.85rem; }
.details-table td { border: 1px solid var(--border); padding: 0.45rem 0.7rem; vertical-align: top; }
.details-table td:first-child { width: 220px; color: var(--text-muted); font-weight: 600; }
.pw-details-table td:first-child { width: 38%; }

/* Timeline diagram (What's Being Valued) */
.tl-svg { width: 100%; height: auto; display: block; }
.tl-tick { stroke: var(--text-muted); stroke-width: 1.5; stroke-dasharray: 2 4; opacity: 0.7; }
.tl-tick-label { fill: var(--heading); font-size: 13px; font-weight: 600; font-family: inherit; }
.tl-pension { stroke: var(--accent-teal); stroke-width: 5; }
.tl-pension-dot { fill: var(--accent-teal); }
.tl-arrow { fill: var(--accent-teal); }
.tl-relationship { stroke: var(--accent-gold); stroke-width: 5; }
.tl-rel-dot { fill: var(--accent-gold); }
.tl-overlap { fill: var(--accent-gold); opacity: 0.32; }
.tl-fp { fill: #9a6a12; font-size: 14px; font-weight: 700; font-family: inherit; }
.tl-bar-label { font-size: 13px; font-weight: 600; font-family: inherit; }
.tl-teal { fill: var(--accent-teal); }
.tl-gold { fill: #9a6a12; }
.tl-caption { font-size: 0.85rem; color: var(--text); margin: 0.75rem 0 0; line-height: 1.45; }

/* Record in Divii */
.record-field { max-width: 460px; }
.record-readout {
  margin-top: 1rem; border: 1px solid var(--border); border-radius: 8px;
  overflow: hidden; max-width: 460px;
}
.record-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0.6rem 0.85rem; font-size: 0.9rem; border-bottom: 1px solid var(--border);
}
.record-row:nth-child(2) { background: var(--accent-gold-bg); }
.record-row strong { font-variant-numeric: tabular-nums; color: var(--heading-strong); }
.record-note { margin: 0; padding: 0.55rem 0.85rem; font-size: 0.82rem; color: var(--accent-teal); background: #eef5f7; }
/* DIVII PATCH: selectable pre/post-tax rows and the Record action */
.record-readout--select { display: flex; flex-direction: column; }
.record-row--option {
  width: 100%; text-align: left; gap: 0.6rem; cursor: pointer;
  background: var(--bg-card); border: 0; border-bottom: 1px solid var(--border);
  font: inherit; color: inherit;
}
.record-row--option:nth-child(2) { background: var(--bg-card); }
.record-row--option:last-child { border-bottom: 0; }
.record-row--option .record-label { flex: 1; }
.record-row--option.selected { background: var(--accent-gold-bg); }
.record-row--option:focus-visible { outline: 2px solid var(--accent-teal); outline-offset: -2px; }
.record-radio { position: relative; flex: 0 0 14px; width: 14px; height: 14px; border-radius: 50%; border: 2px solid var(--accent-teal); }
.record-row--option.selected .record-radio::after { content: ''; position: absolute; inset: 2px; border-radius: 50%; background: var(--accent-teal); }
.record-explain { max-width: 720px; margin-top: 0.9rem; font-size: 0.85rem; color: var(--text); line-height: 1.45; }
.record-explain p { margin: 0 0 0.45rem; }
.record-actions { margin-top: 0.9rem; display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem 0.9rem; max-width: 720px; }
.record-actions .hint { flex-basis: 100%; }
.record-target, .record-warn { flex-basis: 100%; margin: 0; font-size: 0.85rem; line-height: 1.45; }
.record-warn { color: #7a4f00; background: var(--accent-gold-bg); padding: 0.5rem 0.75rem; border-radius: 6px; }
.record-actions .record-note { flex-basis: 100%; border-radius: 6px; }

/* Fields */
.field { margin-bottom: 1rem; }
.field:last-child { margin-bottom: 0; }
.field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 0.35rem;
}
.field input[type="text"],
.field input[type="date"],
.field input[type="number"],
.field select {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  background: var(--bg-page);
  color: var(--text);
  transition: border-color 0.12s, box-shadow 0.12s;
  box-sizing: border-box;
}
.field input:focus,
.field select:focus {
  outline: none;
  border-color: var(--accent-teal);
  box-shadow: var(--focus-ring);
}
.input-prefix {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-input);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.input-prefix:focus-within { border-color: var(--accent-teal); box-shadow: var(--focus-ring); }
.input-prefix span {
  padding: 0.55rem 0.7rem;
  background: var(--bg-card);
  color: var(--text-muted);
  border-right: 1px solid var(--border);
}
.input-prefix input { border: none !important; box-shadow: none !important; flex: 1; background: transparent; }

.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

.slider { width: 100%; accent-color: var(--accent-teal); cursor: pointer; }

/* Pension-status radio toggle */
.radio-row { display: flex; flex-wrap: wrap; gap: 0.4rem 1.5rem; margin-top: 0.1rem; }
.radio-row label {
  display: flex; align-items: center; gap: 0.4rem;
  font-size: 0.9rem; font-weight: 400; color: var(--text);
  cursor: pointer; margin: 0;
}
.radio-row input[type="radio"] {
  appearance: none;
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  margin: 0;
  flex: 0 0 auto;
  border: 2px solid var(--border, #cbd5e1);
  border-radius: 50%;
  background: #fff;
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s ease;
}
.radio-row input[type="radio"]:hover { border-color: var(--accent-teal); }
.radio-row input[type="radio"]:checked {
  border-color: var(--accent-teal);
}
.radio-row input[type="radio"]:checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--accent-teal);
}
.radio-row input[type="radio"]:focus-visible {
  outline: 2px solid var(--accent-teal);
  outline-offset: 2px;
}
.radio-row--stack { flex-direction: column; gap: 0.4rem; }
.wage-growth-field { max-width: 460px; margin-bottom: 1rem; }

.hint { display: block; margin-top: 0.35rem; font-size: 0.8rem; color: var(--text-muted); }

/* Calculated read-outs */
.readout-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem; margin-top: 0.5rem; }
.readout-grid--3 { grid-template-columns: repeat(3, 1fr); }
.pc-inputs--panel .readout-grid--3 { grid-template-columns: 1fr; }
/* Client dropdown + employment salary-row spacing */
.client-field select { max-width: 340px; }
.emp-salary-row { margin-top: 1.25rem; }
.readout {
  background: var(--bg-highlight);
  border-radius: 6px;
  padding: 0.55rem 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.readout--wide { grid-column: 1 / -1; }
.readout-label { font-size: 0.72rem; font-weight: 600; color: var(--heading); text-transform: uppercase; letter-spacing: 0.04em; }
.readout-value { font-size: 1.05rem; font-weight: 600; color: var(--heading-strong); }
.readout-value--sm { font-size: 0.9rem; }
.readout-value small { font-size: 0.75rem; font-weight: 500; color: var(--text-muted); }

.warn-box {
  background: var(--accent-gold-bg);
  border-left: 4px solid var(--accent-gold);
  border-radius: 5px;
  padding: 0.6rem 0.8rem;
  font-size: 0.82rem;
  color: #5b4708;
  margin: 0.5rem 0 0;
}
.error-box {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  padding: 0.7rem 0.9rem;
  font-size: 0.85rem;
  color: var(--danger);
  margin: 0.75rem 0 0;
}

/* Scenarios */
.scenario-fixed { display: flex; flex-direction: column; gap: 0.4rem; }
.scenario-fixed-row {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
}
.scenario-fixed-name { color: var(--text); }
.custom-age-row { display: flex; align-items: flex-end; gap: 0.5rem; margin-bottom: 0.6rem; }
.custom-age-field { flex: 1; margin-bottom: 0; }
.btn-x {
  width: 38px; height: 38px;
  border: 1px solid var(--border-input);
  background: var(--bg-page);
  border-radius: 8px;
  color: var(--danger);
  font-size: 1.1rem;
  cursor: pointer;
  line-height: 1;
}
.btn-x:hover { border-color: var(--danger); background: #fef2f2; }

/* Buttons */
.btn-primary {
  background: var(--cta-bg);
  color: var(--cta-text);
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1.5rem;
  font-size: 0.95rem;
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, transform 0.04s;
}
.btn-primary:hover { background: var(--cta-bg-hover); }
.btn-primary:active { transform: translateY(1px); }
.btn-ghost {
  background: transparent;
  color: var(--accent-teal);
  border: 1px solid var(--border-input);
  border-radius: 8px;
  padding: 0.55rem 1rem;
  font-size: 0.88rem;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
}
.btn-ghost:hover { background: var(--bg-card); border-color: var(--accent-teal); color: var(--heading-strong); }
.btn-block { width: 100%; display: block; }

/* Results */
.results-placeholder {
  background: var(--bg-card);
  border: 1px dashed var(--border-input);
  border-radius: 8px;
  padding: 2.5rem 1.5rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.95rem;
}
.summary-cols {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
  gap: 1rem 1.75rem;
}
.summary-block h4 {
  margin: 0 0 0.5rem;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--accent-teal);
  font-weight: 700;
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.3rem;
}
.summary-block p { margin: 0 0 0.3rem; font-size: 0.85rem; }
.summary-valuation {
  margin: 1rem 0 0;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border);
  font-size: 0.85rem;
}

.table-scroll { overflow-x: auto; }
/* Fixed layout + a fixed metric-column width so every group's table shares
   identical column widths — Scenario 4 in the 2nd table lines up under
   Scenario 1 in the 1st, with empty slots where there's no scenario. */
.results-table { border-collapse: collapse; width: 100%; font-size: 0.85rem; table-layout: fixed; }
.results-table th, .results-table td {
  padding: 0.5rem 0.7rem;
  text-align: center;
  border-bottom: 1px solid var(--border);
  white-space: nowrap;
}
.results-table .metric-col { width: 190px; }
.results-table td:first-child { white-space: normal; }
/* Empty scenario slots (padding to 3 columns) carry the row's normal striping,
   highlight bands, and header fill through the whole table — just no content. */
.results-table thead th {
  background: var(--accent-teal);
  color: #fff;
  font-weight: 600;
}
.results-table .metric-col, .results-table td:first-child {
  text-align: left;
  font-weight: 500;
  color: var(--heading);
  position: sticky;
  left: 0;
  background: var(--bg-page);
}
.results-table thead .metric-col { background: var(--accent-teal); color: #fff; }
.results-table tbody tr:nth-child(even) td { background: var(--bg-row-alt); }
.results-table tbody tr:nth-child(even) td:first-child { background: var(--bg-row-alt); }
.results-table .row-desc td { font-style: italic; color: var(--text-muted); font-size: 0.8rem; white-space: normal; }
.results-table .row-highlight td { background: var(--accent-gold-bg) !important; font-weight: 600; color: var(--heading-strong); }

/* Charts — embedded as the final row of each results table so each pie sits
   directly under its scenario column. */
.result-group { margin-bottom: 1.5rem; }
.result-group:last-of-type { margin-bottom: 0; }
.results-table .chart-row td {
  background: var(--bg-page) !important;
  border-bottom: none;
  padding: 0.85rem 0.5rem 0.25rem;
  vertical-align: top;
  white-space: normal;
}
.results-table .chart-row .chart-row-label {
  font-size: 0.7rem;
  color: var(--text-subtle);
  font-weight: 600;
  text-align: left;
}
/* Fixed-size chart box; canvas is absolutely positioned so it doesn't widen
   the column. Sets the scenario column to a consistent width. */
/* Fill the column (never wider, so it can't overflow) at a compact height. */
.chart-cell { position: relative; width: 100%; max-width: 170px; height: 150px; margin: 0 auto; }
.chart-cell canvas {
  position: absolute;
  top: 0; left: 0;
  width: 100% !important;
  height: 100% !important;
}

.results-actions { display: flex; justify-content: flex-end; gap: 0.75rem; margin-bottom: 1.25rem; }

/* ───────────────────────────────────────────────────────────────────────────
   PRINT WORKSHEET — hidden on screen; @media print reveals only this block.
   ─────────────────────────────────────────────────────────────────────────── */
.print-worksheet { display: none; }
.pw-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 0.75rem; }
.pw-logo { max-height: 52px; }
.pw-header h1 { font-size: 1.5rem; color: #3F6772; margin: 0; }
.pw-intro {
  font-size: 0.8rem;
  line-height: 1.4;
  color: #333;
  margin: 0 0 0.6rem;
}
.pw-disclaimer {
  background: #f8f8f8;
  border-left: 4px solid #e8b340;
  padding: 0.5rem 0.75rem;
  font-style: italic;
  font-size: 0.72rem;
  line-height: 1.35;
  margin-bottom: 0.75rem;
}
.pw-cols { display: flex; gap: 1.5rem; margin-bottom: 0.75rem; }
.pw-col { flex: 1; }
.pw-col h2 {
  background: #3F6772; color: #fff;
  font-size: 0.95rem; padding: 0.35rem 0.6rem; border-radius: 0; margin: 0 0 0.5rem;
}
.pw-col p { margin: 0.2rem 0; font-size: 0.78rem; }
.print-worksheet > h2 { background: #3F6772; color: #fff; font-size: 1rem; padding: 0.35rem 0.6rem; margin: 0.75rem 0 0.5rem; border-radius: 0; }
.pw-table { border-collapse: collapse; width: 100%; }
.pw-table th { background: #5D8A97; color: #fff; padding: 0.3rem; font-size: 0.75rem; text-align: center; }
.pw-table td { padding: 0.3rem; border-bottom: 1px solid #ddd; font-size: 0.75rem; text-align: center; }
.pw-table td:first-child { text-align: left; font-weight: 500; }
.pw-table tr:nth-child(even) td { background: #f2f2f2; }
.pw-table { table-layout: fixed; }
.pw-table th:first-child, .pw-table td:first-child { width: 26%; }
.pw-group { margin-bottom: 0.75rem; page-break-inside: avoid; }
.pw-chart-row td { background: #fff !important; border-bottom: none; text-align: center; vertical-align: top; padding-top: 6px; }
.pw-chart-img { display: block; margin: 0 auto; width: 100%; max-width: 150px; height: auto; }
.pw-footer { margin-top: 0.75rem; font-size: 0.7rem; color: #888; text-align: center; }

/* Required-field marker (red asterisk on required labels). */
.pension-calc .req { color: #dc3545; font-weight: 700; margin-left: 1px; }
/* DIVII FIX — disabled controls must not show the click cursor. */
.pension-calc :disabled,
fieldset.pc-inputs:disabled label { cursor: not-allowed !important; }
</style>

<!-- Unscoped print rules: hide the live UI, show only the worksheet.
     Every rule is gated on body.pension-printing (added by printReport()
     for the duration of the print) so these app-wide selectors cannot
     blank prints triggered by other features on the same page. -->
<style>
@media print {
  body.pension-printing * { visibility: hidden !important; }
  /* Collapse the on-screen calculator UI so its (still-laid-out) height does not
     generate trailing blank pages behind the absolutely-positioned worksheet. */
  body.pension-printing .pension-calc > *:not(.print-worksheet) { display: none !important; }
  body.pension-printing .pension-calc .print-worksheet,
  body.pension-printing .pension-calc .print-worksheet * { visibility: visible !important; }
  body.pension-printing .pension-calc .print-worksheet {
    display: block !important;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    /* Margins come from @page; no extra padding (that doubled the margin and
       pushed content onto an extra page). */
  }
  /* NOTE: @page cannot be gated by a body class; portrait/1cm applies to any
     print from pages where this chunk is loaded. Cosmetic only. */
  @page { size: portrait; margin: 1cm; }
}
</style>
